import * as THREE from './three/three.module.js';
import { EffectComposer } from './three/examples/jsm/postprocessing/EffectComposer.js';
import { UnrealBloomPass } from './three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { MotionBlurPass } from './three/examples/jsm/postprocessing/MotionBlurPass.js';

import { RenderPass } from './three/examples/jsm/postprocessing/RenderPass.js';
/*
import { GlitchPass } from './three/examples/jsm/_postprocessing/GlitchPass.js';
import { OutlinePass } from './three/examples/jsm/_postprocessing/OutlinePass.js';
import { BokehPass } from '../three/examples/jsm/postprocessing/BokehPass.js';
*/
import { SAOPass } from './three/examples/jsm/postprocessing/SAOPass.js';
import { SSAOPass } from './three/examples/jsm/postprocessing/SSAOPass.js';

import { ParticleEngine} from './three/ParticleEngine.js';
import { VRButton } from './three/examples/jsm/webxr/VRButton.js';
import { mergeGeometries, interleaveAttributes } from "../rt/BufferGeometryUtils.js";
import { RT_POINT_LIGHT, RT_SPEC, RT_DIFF, RT_COAT, RT_REFR, EngineBase, smoke_params, candle_params, firework_params, cyber_params, sand_params, shot_params, drift_params, carColors, triTable, RootBone, SimpleBone, SimpleSkeleton } from './engine_base.js';
import { RTEngine } from './engine_rt.js';
import { Transformation3 } from './transform.js';
import { TextGeometry } from './three/examples/jsm/geometries/TextGeometry.js';


class EngineMain extends EngineBase {

    renderer;
    composer;
    postProcessing;
    enableRaytracing;

    constructor(audio = false, assetBase = '') {

        super(audio, assetBase);
        this.shadowTarget = null;

        const w = 320;
        const h = 200;

        this.tmpM = new THREE.Matrix4();

        this.renderer = new THREE.WebGLRenderer();
        this.renderer.setSize(w, h);

        this.camera = new THREE.PerspectiveCamera(this.cameraFOV, w / h, 0.8, 700);
        this.bloom = { strength: 0.9, radius: 0.15, threshold: 0.5 };

        this.CamUIratio1 = 0.7;
        this.CamUIratio2 = 0.7;
        this.CamUIratio3 = 0.35;
        this.renderer.toneMapping = THREE.ReinhardToneMapping;
        this.initScene();

        this.matPool = [];

        this.it = new Transformation3();

        this.tmpV3 = new THREE.Vector3();

        this.scene.add(this.cameraAnchor);

        this.composer = null;

        const postProcessing = localStorage.getItem('postProcessing');

        if (postProcessing === null)
            this.postProcessing = false;
        else {
            this.postProcessing = parseInt(postProcessing) === 1 ? true : false;
        }

        const RayTracing = localStorage.getItem('rayTracing');

        if (RayTracing === null)
            this.enableRaytracing = false;
        else {
            this.enableRaytracing = parseInt(RayTracing) === 1 ? true : false;
        }



        this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        this.sphereGeom = new THREE.SphereGeometry(1, 32, 16);
        this.cylinderGeometry = new THREE.CylinderGeometry(1, 1, 1, 32, 16);
        this.initRTReflections(this.enableRaytracing);
    }

    async initRTReflections(on = true) {

        if (on) {

            if (this.rtEngine)
                return;

            this.rtEngine = new RTEngine(this.renderer);
            await this.rtEngine.initRT();
        } else {

            if (this.rtEngine) {
                this.rtEngine.disableAllRT();
                this.rtEngine = null;
            } else {
                /*
                initShaders();
                try{
                    const fs = await fetch('/assets/shaders/rt_refl.glsl');
                    this.pathTracingFragmentShader = await fs.text();
                }catch(e){
                    console.log('unable to init rt '+e.message);
                    return;
                }
                */
                THREE.ShaderChunk['pathtracing_main'] = '';


            }

            this.rtEngine = null;
        }
    }

    initVR(enableVRFunc) {
        this.renderer.xr.enabled = true;
        this.renderer.xr.setReferenceSpaceType('local');

        this.myVRButton = VRButton.createButton(this.renderer);
        document.body.append(this.myVRButton);

        this.renderer.xr.addEventListener('sessionstart', function (event) { enableVRFunc(true); });
        this.renderer.xr.addEventListener('sessionend', function (event) { enableVRFunc(false); });
    }

    enableCameraAnchor(on) {

        if (on)
            this.cameraAnchor.add(this.camera);
        else
            this.cameraAnchor.remove(this.camera);
    }

    initXRControllers(session) {

        if (!session)
            return;

        //a check to prevent console errors if only one input source
        if (!session.inputSources)
            return;

        let handedness;

        for (let i = 0; i < session.inputSources.length; i++) {

            if (this.hasVrPad(i))
                continue;

            const source = session.inputSources[i];

            if (source && source.handedness) {
                handedness = source.handedness; //left or right controllers
            }

            if (source.gamepad)
                this.addVrGamepad(i, source.gamepad);
        }

    }

    initControllers() {
        const self = this;

        window.addEventListener('gamepadconnected', (e) => self.handleGamePadConnected(e));
        window.addEventListener('gamepaddisconnected', (e) => self.handleGamePadDisconnected(e));

        const length = 10.0;
        let geometry = new THREE.BufferGeometry();
        let tgeometry = new THREE.CylinderGeometry(1.0, 1.0, 0.2);
        tgeometry.computeBoundingBox();

        let vertices = [0, 0, 0, 0, 0, -length];
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

        let raymaterial = new THREE.LineBasicMaterial({ color: 0xFFFFFFFF });

        this.vrRay1 = new THREE.Line(geometry, raymaterial);
        this.vrRay2 = new THREE.Line(geometry, raymaterial);

        this.vrTeleport1 = new THREE.Mesh(tgeometry, raymaterial);
        this.vrTeleport2 = new THREE.Mesh(tgeometry, raymaterial);

        this.controller1 = this.renderer.xr.getController(0);
        this.controller2 = this.renderer.xr.getController(1);

        /*
        this.controller1.addEventListener( 'connected', (e) => {
            this.controller1.gamepad = e.data.gamepad;
            this.XRPads.push(this.controller1);
                //const hand = this.controller1.position;
                //const str = JSON.stringify(hand);
                //$('#gl-dom').html('gamepad 1 '+str);
                
        });

        this.controller2.addEventListener( 'connected', (e) => {
            this.controller2.gamepad = e.data.gamepad;
            this.XRPads.push(this.controller2);
            
            //const hand = this.controller2.position;
            //const str = JSON.stringify(hand);
            //$('#gl-dom').append('gamepad 2 '+str);
            
        });
        */

        /*
            this.XRSession = await XR.requestSession("inline");
            for(let isrc of this.XRSession.inputSources){
                if(isrc.gamepad){
                    this.addGamePad(isrc.gamepad);
                    this.XRPads.push(isrc.gamepad);
                }
            }
        */
    }

    enableRender(on, glDomRef) {

        if (glDomRef.current) {

            if (on) {

                this.resizeCanvasToDisplaySize(glDomRef.current.clientWidth, glDomRef.current.clientHeight);
                
                if (glDomRef.current.children.length < 1) {
                    glDomRef.current.appendChild(this.renderer.domElement);
                }

                

            } else {

                glDomRef.current.removeChild(this.renderer.domElement);
            }
        }
    }
    destroyTex(texture) {
        texture.dispose();
    }

    writeToTex(pos, data, tex) {
        tex.needsUpdate = true;
    }

    copyTexToTex(pos, src, dst) {

        this.renderer.copyTextureToTexture(pos, src, dst, 0);
        dst.needsUpdate = true;
    }
    setLightIntensity(sp, i) {
        sp.light.intensity = i;
    }

    addRectLight(sp, pos, target, color, width, height, i) {
        sp.light = new THREE.RectAreaLight(new THREE.Color().setRGB(color.x, color.y, color.z).getHex(), i, width, height);
        sp.light.position.set(pos.x, pos.y, pos.z);
        sp.light.lookAt(target);
        this.scene.add(sp.light);
    }


    addPointLight(sp, pos, color, radius) {

        sp.light = new THREE.PointLight(new THREE.Color().setRGB(color.x, color.y, color.z).getHex(), 1, radius, 0);
        sp.light.position.set(pos.x, pos.y, pos.z);
        this.scene.add(sp.light);

    }

    addDirLight(sp, dir, color) {

        sp.light = new THREE.DirectionalLight(new THREE.Color().setRGB(color.x, color.y, color.z).getHex(), 8.2);
        sp.light.position.set(-dir.x, -dir.y, -dir.z);

        const geo = new THREE.SphereGeometry(0.1);
        sp.light.target = new THREE.Mesh(geo);

        sp.light.target.name = 'dir light target';
        sp.light.name = 'dir light';

        this.scene.add(sp.light.target);
        this.scene.add(sp.light);


    }

    removeLight(sp) {

        this.scene.remove(sp.light);

        if (sp.target)
            this.scene.remove(sp.target);
    }

    addSpotLight(sp, pos, dir, color, radius, aa) {

        sp.light = new THREE.SpotLight(new THREE.Color().setRGB(color.x, color.y, color.z).getHex(), 8.0, radius, Math.PI / 4, 0.01, 0.85);
        sp.light.position.set(pos.x, pos.y, pos.z);
        sp.power = 4.0;

        sp.geom = new THREE.BoxGeometry(0.001, 0.001, 0.001, 1, 1, 1);
        sp.target = new THREE.Mesh(sp.geom, this.SphereMaterial);
        sp.target.name ='spot light target';
        sp.light.name ='spot light';
        sp.target.position.set(pos.x + dir.x * 1.0, pos.y + dir.y * 1.0, pos.z + dir.z * 1.0);

        sp.light.target = sp.target;
        this.scene.add(sp.target);
        this.scene.add(sp.light);

        //const pointLightHelper = new THREE.SpotLightHelper( sp.light );
        //this.scene.add( pointLightHelper );

    }

    setLightPosition(c, x, y, z, r) {

        if (c.light) {

            if (x !== null)
                c.light.position.set(x, y, z);

            c.light.distance = r;
        }
    }
    setLightDir(c, x, y, z, a) {

        if ((c.light) && (c.target)) {
            c.target.position.set(c.light.position.x - x * 10.0, c.light.position.y - y * 10.0, c.light.position.z - z * 10.0);
        }
    }


    setLightColor(c, r, g, b) {

        if (c.light) {
            const col = new THREE.Color().setRGB(r, g, b);
            c.light.color.setRGB(col.r, col.g, col.b);
        }
    }

      removeParticles(p) {

        let n = 0;

        while (n < this.particles.length) {
            if (p.id == this.particles[n].id) {
                this.scene.remove(this.particles[n].particleMesh);
                this.particles[n].particleMesh.geometry.dispose();

                if(this.particles[n].particleTexture)
                    this.particles[n].particleTexture.dispose();

                //this.particles[n].particleMaterial.dispose();
                this.particles.splice(n, 1);
                return;
            }
            n++;
        }
    }


    removeAllParticles() {

        let p;

        while ((p = this.particles.pop()) != null) {
            this.scene.remove(p.particleMesh);
            p.particleMesh.geometry.dispose();
            p.particleTexture.dispose();
            p.particleMaterial.dispose();
        }

    }


    removePointLight(sp) {
        if (sp.light == null)
            return;

        this.scene.remove(sp.light);
        sp.light.dispose();
        sp.light = null;
    }

    initScene() {
        this.hiddenObjects = [];
        this.particles = [];
        this.sceneNOPP = new THREE.Scene();
        this.scene = new THREE.Scene();

        if (this.skyTexture) {
            this.scene.background = this.skyTexture;
            this.scene.environment = this.skyTexture;
        }
    }

    compileShaders() {

        this.setAllCulled(this.scene, true);

        this.renderer.compile(this.scene, this.camera);

        this.setAllCulled(this.scene, false);
    }

    sortRTArray(maxDist) {


        const indexes = new Uint16Array(this.rtEngine.nGeometries);
        for (let i = 0; i < this.rtEngine.nGeometries; i++) {
            indexes[i] = i;
        }

        const pos1 = new THREE.Vector3();
        const pos2 = new THREE.Vector3();

        indexes.sort(function (a, b) {
            pos1.set(this.rtEngine.matrix_array[a * 16 + 13], this.rtEngine.matrix_array[a * 16 + 14], this.rtEngine.matrix_array[a * 16 + 15]);
            pos2.set(this.rtEngine.matrix_array[b * 16 + 13], this.rtEngine.matrix_array[b * 16 + 14], this.rtEngine.matrix_array[b * 16 + 15]);

            if (pos1.z < pos2.z)
                return -1;

            if (pos1.z > pos2.z)
                return 1;

            return 0;
        });

        const tmpArray = this.rtEngine.matrix_array.clone();
        for (let i = 0; i < this.rtEngine.nGeometries; i++) {

        }


    }


    updateTransform(m, t, iid = -1) {

        if (!this.rtEngine)
            return;

        if (!m.rtMesh)
            return;

        const gm = m.rtMesh.length >0 && iid >=0 ? m.rtMesh[iid]: m.rtMesh;
        if ( gm.geoID == null) 
            return;

        this.rtEngine.updateTransform(gm, t);
                
        //const matArray = this.rtEngine.matrix_array.subarray(m.rtMesh.geoID * 32, (m.rtMesh.geoID + 1) * 32);
        

    }

    updateBoneTransform(m, boneName, q) {

        const o = m.object ? m.object : m;

        if (!o.skeleton)
            return;

        const bone = o.skeleton.getBoneByName(boneName);
        if (bone) {
            bone.quaternion.set(q.x, q.y, q.z, q.w);
            bone.updateMatrix();
        }
    }

    vfovToHfov(vfov, aspect) {
        const { tan, atan } = Math
        return atan(aspect * tan(vfov / 2)) * 2
    }

    /**
     * Get the distance from the camera to fit an object in view by either its
     * horizontal or its vertical dimension.
     *
     * @param size - This should be the width or height of the object to fit.
     * @param fov - If `size` is the object's width, `fov` should be the horizontal
     * field of view of the view camera. If `size` is the object's height, then
     * `fov` should be the view camera's vertical field of view.
     * @returns - The distance from the camera so that the object will fit from
     * edge to edge of the viewport.
     */
    _distanceToFitObjectInView(size, fov) {
        const { tan } = Math
        return size / (2 * tan(fov / 2))
    }

    distanceToFitObjectToView(cameraAspect, cameraVFov, objWidth, objHeight) {
        const objAspect = objWidth / objHeight
        const cameraHFov = this.vfovToHfov(cameraVFov, cameraAspect)

        let distance = 0

        if (objAspect > cameraAspect) {
            distance = this._distanceToFitObjectInView(objHeight, cameraVFov)
        } else if (objAspect <= cameraAspect) {
            distance = this._distanceToFitObjectInView(objWidth, cameraHFov)
        }

        return distance
    }

    updateCameraSpaceMesh(mesh, pos) {

        const rfov = this.camera.fov * Math.PI / 180.0;
        const fovf = 2 * Math.tan(rfov / 2);

        const offset = this.tmpV3.set(pos.x * fovf * this.camera.aspect + mesh.ox * fovf, pos.y * fovf + mesh.oy * fovf, pos.z).applyQuaternion(this.cameraAnchor.quaternion);
        offset.add(this.cameraAnchor.position);

        mesh.scale.set(mesh.sx * fovf, mesh.sy * fovf, 1.0);
        mesh.position.set(offset.x, offset.y, offset.z);
        mesh.quaternion.set(this.cameraAnchor.quaternion.x, this.cameraAnchor.quaternion.y, this.cameraAnchor.quaternion.z, this.cameraAnchor.quaternion.w);
    }


    addCameraSpaceMesh(mesh, pos, camSpace = true) {

        if (mesh === null)
            return;

        if (camSpace === true) {
            const offset = this.tmpV3.set(pos.x, pos.y, pos.z).applyQuaternion(this.cameraAnchor.quaternion);
            offset.add(this.cameraAnchor.position);
            mesh.position.set(offset.x, offset.y, offset.z);
            mesh.quaternion.set(this.cameraAnchor.quaternion.x, this.cameraAnchor.quaternion.y, this.cameraAnchor.quaternion.z, this.cameraAnchor.quaternion.w);
            mesh.renderOrder = 100;
            this.CameraSpaceScene.add(mesh);
        } else {
            mesh.position.set(pos.x, pos.y, pos.z);
            mesh.renderOrder = 100;
        }

        mesh.sx = 1.0;
        mesh.sy = 1.0;

        mesh.ox = 0.0;
        mesh.oy = 0.0;

        mesh.updateMatrixWorld(true);


    }

    addMesh(m, matType, transformation, scale, useTex) {

        m.traverse(function (c) {
            if (!c.isMesh)
                return;

            if (c.material.length) {
                for (let m of c.material) {

                    /*
                    if(m.name === 'ytun'){
                        m.transmission = 1.0;
                        m.thickness = 0.4;
                        m.ior = 2.4;
                    }
                    */

                    m.transmission = 0.0;

                    if ((m.emissive) && ((m.emissive.r > 0) || (m.emissive.b > 0) || (m.emissive.g > 0))) {
                        m.toneMapped = false;
                        //m.emissiveIntensity = 8.0;
                        /*console.log(m.name);*/
                    }
                }
            } else {
                const m = c.material;

                m.transmission = 0.0;

                if ((m.emissive) && ((m.emissive.r > 0) || (m.emissive.b > 0) || (m.emissive.g > 0))) {
                    m.toneMapped = false;
                    //m.emissiveIntensity = 8.0;
                    //console.log(m.name);
                }

            }
        })

        this.scene.add(m);
    }


    setCylinderPos(c, radius, center, height, quat) {

        if (!c.object)
            return;

        c.object.position.set(center.x, center.y, center.z);
        c.object.quaternion.set(quat.x, quat.y, quat.z, quat.w);

    }
    addCylinder(c, radius, position, height, quat, color, roughness, type, tex = null, norm = null) {
        const col = new THREE.Color(color.x, color.y, color.z);

        if (type == RT_REFR) {
            c.mat = new THREE.MeshPhysicalMaterial({ color: col.getHex(), transparent: true });
            c.mat.transmission = 1.0;
            c.mat.thickness = 0.4;
            c.mat.reflectivity = 0.3;
            c.mat.metalness = 0.0;
            c.mat.ior = 1.3;
        } else {
            c.mat = new THREE.MeshPhysicalMaterial({ color: col.getHex(), side: THREE.DoubleSide, depthWrite: true, map: tex, normalMap: norm });
        }
        c.mat.roughness = roughness;
        c.object = new THREE.Mesh(this.cylinderGeometry, c.mat);
        c.object.position.set(position.x, position.y, position.z);
        c.object.quaternion.set(quat.x, quat.y, quat.z, quat.w);
        c.object.scale.set(radius, height, radius);

        this.addMesh(c.object);
    }

    compareCol(col1, col2) {
        if (Math.abs(col2.r - col1.r) > 0.01)
            return false;

        if (Math.abs(col2.g - col1.g) > 0.01)
            return false;

        if (Math.abs(col2.b - col1.b) > 0.01)
            return false;

        return true;

    }

    findMat(color, emm, roughness) {

        for (let m of this.matPool) {
            if ((this.compareCol(m.color, color)) &&
                (this.compareCol(m.emissive, emm)) &&
                (Math.abs(m.roughness - roughness) < 0.01))
                return m;
        }
        const mat = new THREE.MeshPhongMaterial({ color: color.getHex(), side: THREE.DoubleSide, depthWrite: true });
        mat.emissive.setRGB(emm.r, emm.g, emm.b);
        mat.roughness = roughness;

        if ((mat.emissive.r > 0.1) || (mat.emissive.b > 0.1) || (mat.emissive.g > 0.1)) {
            //mat.emissiveIntensity = 8.0;
            mat.toneMapped = false;
            mat.emissiveIntensity = 8.0;
        }

        this.matPool.push(mat);
        return mat;

    }

    createParticles(setting, pos, pinned = false){
        const particles = new ParticleEngine(pos, pinned);
        particles.setValues(setting);
        particles.initialize();
        particles.positionBase.set(pos.x, pos.y, pos.z);

        this.scene.add( particles.particleMesh );
        this.particles.push(particles);

        return particles;
    }
  

    addSphereInstance(d, c, x, y, z) {

        d.mat = c.mat;
        d.object = c.object.clone();
        d.object.position.set(x, y, z);

        this.addMesh(d.object);
    }


    addSphere(c, radius, position, emission, color, roughness, type) {

        this.tmpcol.setRGB(color.x, color.y, color.z);
        this.tmpemm.setRGB(emission.x, emission.y, emission.z);

        if (type == RT_REFR) {
            c.mat = new THREE.MeshPhysicalMaterial({ color: this.tmpcol.getHex(),  emissive:this.tmpemm.getHex(), transparent: false });
            c.mat.transmission = 1.0;
            c.mat.emissive.setRGB(this.tmpemm.r, this.tmpemm.g, this.tmpemm.b);
            c.mat.thickness = 0.4;
            c.mat.reflectivity = 0.3;
            c.mat.metalness = 0.1;
            c.mat.roughness = 0.1;
            c.mat.depthWrite = false;
            c.mat.ior = 1.3;
        } else {
            c.mat = this.findMat(this.tmpcol, this.tmpemm, roughness);
        }


        c.object = new THREE.Mesh(this.sphereGeom, c.mat);
        c.object.position.set(position.x, position.y, position.z);
        c.object.scale.set(radius, radius, radius);
        c.object.renderOrder = 1;
        c.object.name= "sphere-"+radius;

        this.addMesh(c.object);
    }

    moveSpherePosition(c, x, y, z, r) {
        if (!c.object)
            return;

        if (x)
            c.object.position.x += x;
        if (y)
            c.object.position.y += y;
        if (z)
            c.object.position.z += z;

        c.object.scale.set(r, r, r);
    }

    setSpherePosition(c, x, y, z, r) {

        if (!c.object)
            return;

        if (x !== null)
            c.object.position.x = x;

        if (y !== null)
            c.object.position.y = y;

        if (z !== null)
            c.object.position.z = z;

        c.object.scale.set(r, r, r);

    }

    setSphereEmissive(c, r, g, b) {
        c.object.material.emissive.setRGB(r !== null ? r : c.object.material.color.r, g !== null ? g : c.object.material.color.g, b !== null ? b : c.object.material.color.b);
        c.object.material.needsUpdate = true;
    }

    setSphereColor(c, r, g, b, a = 1.0) {
        c.object.material.color.setRGB(r ? r : c.object.material.color.r, g ? g : c.object.material.color.g, b ? b : c.object.material.color.b);

        if ((a) && (a > 0.01)) {
            c.object.material.opacity = a;
            c.object.material.transparent = true;
        }


        c.object.material.needsUpdate = true;
    }


    removeBox(c) {
        if (c.object)
            this.scene.remove(c.object);

        if (c.geom)
            c.geom.dispose();

        if (c.mat)
            c.mat.dispose();
    }

    removeSphere(c, rem = true) {
        if (c.object)
            this.scene.remove(c.object);

        if (this.rtEngine) {

            if (!c.object)
                return;

            if (c.object.rtMesh) {
                this.rtEngine.removeMesh(c.object.rtMesh);
                c.object.rtMesh = null;
            }
        }

        if (!rem)
            return;

        /*
        if (c.geom)
            c.geom.dispose();

        if (c.mat)
            c.mat.dispose();
        */
    }

    removeCylinder(c) {
        if (c.object)
            this.scene.remove(c.object);

        if (c.geom)
            c.geom.dispose();

        if (c.mat)
            c.mat.dispose();
    }

    removeSprite(m) {
        if (m)
            this.sceneUI.remove(m);
    }
    removeMesh(m) {
        if (m)
            this.scene.remove(m);
    }
    removeCameraSpaceMesh(m) {
        if (m)
            this.CameraSpaceScene.remove(m);
    }

    addBox(c, size, position, color, roughness, type) {
        c.geom = new THREE.BoxGeometry(size.x, size.y, size.z, 1, 1, 1);
        if (type == RT_REFR) {
            c.mat = new THREE.MeshPhysicalMaterial({ color: color, transparent: true });
            c.mat.transmission = 1.0;
            c.mat.thickness = 0.1;
            c.mat.roughness = 0.0;
            c.mat.reflectivity = 0.1;
            c.mat.metalness = 0.0;
            c.mat.ior = 3;
        } else {
            c.mat = new THREE.MeshBasicMaterial({ color: color, transparent: true });
        }

        c.object = new THREE.Mesh(c.geom, c.mat);
        c.object.position.set(position.x, position.y, position.z);

        this.addMesh(c.object);
    }




    async removePod() {
        this.scene.remove(this.podMesh);
    }


    addCyber(pos) {
        const particles = new ParticleEngine(pos);
        cyber_params.positionBase = pos;
        particles.setValues(cyber_params);
        particles.initialize();

        this.scene.add(particles.particleMesh);

        this.particles.push(particles);
    }

    addSand(pos) {
        const particles = new ParticleEngine(pos);
        sand_params.positionBase = pos;
        particles.setValues(sand_params);
        particles.initialize();

        this.scene.add(particles.particleMesh);

        this.particles.push(particles);
    }

    addFire(pos) {

        const particles = new ParticleEngine(pos);
        candle_params.positionBase = pos;
        particles.setValues(candle_params);
        particles.initialize();

        this.scene.add(particles.particleMesh);

        this.particles.push(particles);
    }

    addFirework(pos) {

        const particles = new ParticleEngine(pos);
        firework_params.positionBase = pos;
        particles.setValues(firework_params);
        particles.initialize();

        this.scene.add(particles.particleMesh);

        this.particles.push(particles);

    }


    addFog(density = 0.1) {
        this.scene.fog = new THREE.FogExp2(0xAAAAAAA, density);
    }

    addRain() {
        this.scene.add(this.rainMesh);
        this.scene.add(this.rainDropMesh);
    }

    removeRain() {
        this.scene.fog = null;

        if (this.rainMesh !== null) {
            this.scene.remove(this.rainMesh);
            this.rainMesh.geometry.dispose();
        }
    }

    createPathStrings(filename) {
        const basePath = "./assets/textures/";
        const baseFilename = basePath + filename;
        const fileType = ".jpg";
        const sides = ["ft", "bk", "up", "dn", "rt", "lf"];

        const pathStings = sides.map(side => {
            return baseFilename + "_" + side + fileType;
        });

        return pathStings;
    }

    createMaterialArray(filename) {
        const skyboxImagepaths = this.createPathStrings(filename);

        const materialArray = skyboxImagepaths.map(image => {
            let texture = this.texLoader.load(this.assetBase+image);
            const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.BackSide }); // <---
            mat.depthTest = false;
            mat.depthWrite = false;

            return mat;
        });

        return materialArray;
    }

    async initEnvMap() {
        const ldrUrls = ['afterrain_rt.jpg', 'afterrain_lf.jpg', 'afterrain_up.jpg', 'afterrain_dn.jpg', 'afterrain_ft.jpg', 'afterrain_bk.jpg'];

        this.generator = new THREE.PMREMGenerator(this.renderer);
        await this.generator.compileCubemapShader();

        this.ldrCubeMap = await new THREE.CubeTextureLoader().setPath(this.assetBase+'/assets/textures/').loadAsync(ldrUrls);
        //this.scene.background = this.ldrCubeMap;
    }

    async addSkyBox(skyboxImage) {

        const url = this.hdSkybox ? "/assets/skybox/" + skyboxImage: "/assets/skybox/small/" + skyboxImage

        this.skyTexture = await this.texLoader.loadAsync(this.assetBase+url);
        this.skyTexture.mapping = THREE.EquirectangularReflectionMapping;
        this.skyTexture.encoding = THREE.sRGBEncoding;

        if (this.skyTexture) {
            this.scene.background = this.skyTexture;
            this.scene.environment = this.skyTexture;
        }

        this.sc = false;
    }

    addTrackRope(rope, object) {

        this.makeRope(rope, object);

        this.scene.add(rope.anchor.object);
        this.scene.add(rope.obj.object);
    }

    addTrackSpring(spring, object) {

        this.makeSpring(spring, object);

        this.scene.add(spring.anchor.object);
        this.scene.add(spring.obj.object);
    }


    addTrackbonus(bonus, object = null) {
        this.makeBonus(bonus, object);
        this.scene.add(bonus.light);
        this.scene.add(bonus.object);
    }




    async addTrackJumper(jumper, object = null) {

        await this.makeJumper(jumper, object);
        this.scene.add(jumper.object);
    }

    async addTrackTurret(turret) {

        await this.makeTurret(turret);

        turret.head.add(turret.canon);
        this.scene.add(turret.head);
        this.scene.add(turret.object);

        if (this.rtEngine) {
            turret.object.rtMesh = this.rtEngine.addMesh(turret.object, RT_COAT, new Transformation3(turret.object.position, turret.object.quaternion));
            this.rtEngine.drawMesh(turret.object.rtMesh);
            this.rtEngine.alwaysRender.push(turret.object.rtMesh.geoID);

            turret.head.rtMesh = this.rtEngine.addMesh(turret.head, RT_COAT, new Transformation3(turret.head.position, turret.head.quaternion));
            this.rtEngine.drawMesh(turret.head.rtMesh);
            this.rtEngine.alwaysRender.push(turret.head.rtMesh.geoID);
        }

        if (turret.shotObject) {
            this.scene.add(turret.shotObject);
        }



    }

    async addTrackCharacter(char) {

        await this.makeCharacter(char);

        if ((char.skeleton) && (char.originalMesh === null)) {
            char.originalMesh = char.avatarMesh.clone();
            const infos = this.bindSkeleton(char.skeleton, char.originalMesh);
            char.tskel = infos.skel;
            char.avatarMesh = infos.mesh;
        }
        this.addMesh(char.avatarMesh);
    }




    updateCharacterSkeleton(char) {

        if (char.avatarMesh) {
            this.removeMesh(char.avatarMesh);
        }

        const infos = this.bindSkeleton(char.skeleton, char.originalMesh);
        char.tskel = infos.skel;
        char.avatarMesh = infos.mesh;

        this.addMesh(char.avatarMesh);
    }

    removeTrackCharacter(char) {

        if (char.avatarMesh) {
            this.removeMesh(char.avatarMesh);
        }
    }

    addMeshChild(parent, child) {
        parent.add(child);
    }

    removeMeshChild(parent, child) {
        parent.remove(child);
    }

    addDMGBar(dmgBar){

        if(!this.DMGBarsInstanceMesh){

            this.IDMGmaterial = new THREE.MeshBasicMaterial({ color: 0xFFFFFF});
            this.DMGBarsInstanceMesh= new THREE.InstancedMesh(this.DMGgeo1, this.IDMGmaterial, this.numDmgBar * 2);
            this.DMGBarsInstanceMesh.frustumCulled = false;
            this.DMGBarsInstanceMesh.name = 'dmg bar instances';
            this.scene.add(this.DMGBarsInstanceMesh);
        }

        this.tmpQ.set(0, 0, 0, 1);

        this.tmpM.compose(dmgBar.position, this.tmpQ, dmgBar.scale1)
        this.tmpM.lookAt(dmgBar.position, this.cameraAnchor.position, this.upVector);
        this.DMGBarsInstanceMesh.setMatrixAt(dmgBar.instanceID * 2 + 0, this.tmpM);
        this.DMGBarsInstanceMesh.setColorAt(dmgBar.instanceID * 2 + 0, dmgBar.color1)

        this.tmpM.compose(dmgBar.position, this.tmpQ, dmgBar.scale2)
        this.tmpM.lookAt(dmgBar.position, this.cameraAnchor.position, this.upVector);
        this.DMGBarsInstanceMesh.setMatrixAt(dmgBar.instanceID * 2 + 1, this.tmpM);
        this.DMGBarsInstanceMesh.setColorAt(dmgBar.instanceID * 2 + 1, dmgBar.color2);

         this.DMGBarsInstanceMesh.instanceColor.needsUpdate = true;

        //this.addMesh(dmgBar.DMGBar1);
        //this.addMesh(dmgBar.DMGBar2);
    }


    addTrackDrone(drone) {
       

        if(this.droneMeshCache[drone.model].instanceMesh == null){
            this.droneMeshCache[drone.model].instanceMesh = new THREE.InstancedMesh( this.droneMeshCache[drone.model].bodyGeometry, this.droneMeshCache[drone.model].material, this.droneMeshCache[drone.model].nInstances)
            this.droneMeshCache[drone.model].instanceMesh.castShadow = true;
            this.droneMeshCache[drone.model].instanceMesh.receiveShadow = true;
            this.droneMeshCache[drone.model].instanceMesh.frustumCulled = false;

            this.scene.add(this.droneMeshCache[drone.model].instanceMesh);
        }

        drone.object = this.droneMeshCache[drone.model].instanceMesh;
        drone.instanceID =  this.droneMeshCache[drone.model].usedInstances++;

        this.scene.add(drone.positionalAudio);

        const q = new THREE.Quaternion().setFromEuler(drone.angles);
        this.scaleVec.set(1,1,1);
        this.tmpM.identity();
        this.tmpM.compose(drone.pos, q, this.scaleVec);
        drone.object.setMatrixAt( drone.instanceID, this.tmpM );

        
        /*
        drone.object = new THREE.Mesh(this.droneMeshCache[drone.model].bodyGeometry, this.droneMaterialCache);
        drone.object.position.set(drone.pos.x, drone.pos.y, drone.pos.z);
        drone.object.rotation.set(drone.angles.x, drone.angles.y, drone.angles.z);
        */
        //drone.object.add(drone.positionalAudio);
        /*
        if (drone.object.SimpleSkeleton) {
            const tskel = new THREE.Skeleton(drone.object.SimpleSkeleton.getThree());
            const skinnedMesh = new THREE.SkinnedMesh(drone.object.geometry, drone.object.material);
            skinnedMesh.add(tskel.bones[0]);
            skinnedMesh.bind(tskel);
            skinnedMesh.position.set(drone.object.position.x, drone.object.position.y, drone.object.position.z);
            drone.object = skinnedMesh;
        }
        */

        if (this.rtEngine) {
            drone.rtMesh[drone.instanceID] = this.rtEngine.addMeshInstance(this.droneMeshCache[drone.model].rtMesh, new Transformation3(drone.pos, q));
            this.rtEngine.drawMesh( drone.rtMesh[drone.instanceID]);
            this.rtEngine.alwaysRender.push( drone.rtMesh[drone.instanceID].geoID);
        }

        for (let p=0; p<drone.pales.length;p++) {

            const pale = drone.pales[p];

            if(this.droneMeshCache[drone.model].pales[p].instanceMesh == null){
                this.droneMeshCache[drone.model].pales[p].instanceMesh = new THREE.InstancedMesh( this.droneMeshCache[drone.model].pales[p].paleGeo, this.droneMeshCache[drone.model].pales[p].material, this.droneMeshCache[drone.model].pales[p].nInstances)
                this.droneMeshCache[drone.model].pales[p].instanceMesh.castShadow = true;
                this.droneMeshCache[drone.model].pales[p].instanceMesh.receiveShadow = true;
                this.droneMeshCache[drone.model].pales[p].instanceMesh.frustumCulled = false;
                this.scene.add(this.droneMeshCache[drone.model].pales[p].instanceMesh);
            }

            pale.instanceID =  this.droneMeshCache[drone.model].pales[p].usedInstances++;
            pale.mesh = this.droneMeshCache[drone.model].pales[p].instanceMesh;

   
            const q = new THREE.Quaternion();
            this.scaleVec.set(1,1,1);
            this.tmpM.identity();
            this.tmpM.compose(pale.pos, q, this.scaleVec);
            pale.mesh.setMatrixAt( pale.instanceID, this.tmpM );

            //pale.mesh = new THREE.Mesh(this.droneMeshCache[drone.model].pales[p].paleGeo, this.droneMaterialCache);

            //const dt = new Transformation3(pale.pos);
            //this.addMesh(pale.mesh, RT_COAT, dt);

            if (this.rtEngine) {
                pale.rtMesh[pale.instanceID] = this.rtEngine.addMeshInstance(this.droneMeshCache[drone.model].pales[p].rtMesh, new Transformation3(pale.pos));
                this.rtEngine.drawMesh(pale.rtMesh[pale.instanceID]);
                this.rtEngine.alwaysRender.push(pale.rtMesh[pale.instanceID].geoID);
            }

            pale.dmgBar = this.createDMGBar(pale.pos, new THREE.Vector3(0,2.2,0.0), pale.life);
        }

        for (let wing of drone.wings) {
            drone.object.add(wing);
        }
       
        //this.scene.add(drone.object);

        for (let w of drone.weapons) {
            const pt = new THREE.Vector3(w.projPts.x, w.projPts.y, w.projPts.z).applyQuaternion(drone.object.quaternion).add(drone.object.position);
            w.particles = new ParticleEngine(pt);
            shot_params.positionBase = pt;
            w.particles.name = 'drone-parts';
            w.particles.emitterAlive = false;
            w.particles.pinned = true;
            w.particles.setValues(shot_params);
            w.particles.initialize();
            this.scene.add(w.particles.particleMesh);
            this.particles.push(w.particles);


            if (w.shotObject) {
                w.shotObject.position.set(-10000, -10000, -10000);
                this.scene.add(w.shotObject);
            }
        }
    }

    delTrack(track) {

        if (track.roadObj !== null) {
            this.scene.remove(track.roadObj);
            track.roadObj.geometry.dispose();
        }

        this.scene.remove(this.DMGBarsInstanceMesh);
        this.DMGBarsInstanceMesh.geometry.dispose();
        this.DMGBarsInstanceMesh=null;

        this.numDmgBar = 0;

        for (let n = 1; n < track.walls.length; n++) {
            this.scene.remove(track.walls[n].object);
            if(this.rtEngine){
                this.rtEngine.removeMesh(track.walls[n].object.rtMesh);
            }
        }

        if(this.rtEngine){
            this.rtEngine.removeMesh(track.walls[0].object.rtMesh);
        }
        this.scene.remove(track.walls[0].object);
        this.destroyObj(track.walls[0].object);


        if (track.woods) {
            for (let n = 0; n < track.woods.length; n++) {
                for (let i = 0; i < track.woods[n].objects.length; i++) {
                    this.scene.remove(track.woods[n].objects[i].object);
                    track.woods[n].objects[i].object.geometry.dispose();
                }
            }
        }

        if (track.bonuses) {
            for (let n = 0; n < track.bonuses.length; n++) {

                this.scene.remove(track.bonuses[n].object);
                this.scene.remove(track.bonuses[n].light);

                track.bonuses[n].object.geometry.dispose();
                track.bonuses[n].light.dispose();
            }
        }

        if (track.jumpers) {
            for (let n = 0; n < track.jumpers.length; n++) {
                this.scene.remove(track.jumpers[n].object);
                track.jumpers[n].object.geometry.dispose();
            }
        }


        if (track.turrets) {
            for (let n = 0; n < track.turrets.length; n++) {

                for (let i = 0; i < track.turrets.shots.length; i++) {
                    this.scene.remove(track.turrets[n].shots[i].object);
                    track.turrets[n].shots[i].object.geometry.dispose();
                }

                this.scene.remove(track.turrets[n].object);
                track.turrets[n].object.geometry.dispose();
            }
        }
        if (track.grids) {
            for (let n = 0; n < track.grids.length; n++) {
                this.scene.remove(track.grids[n].mesh);
                this.scene.remove(track.grids[n].fallBall.mesh);

                track.grids[n].mesh.geometry.dispose();
                track.grids[n].fallBall.mesh.geometry.dispose();

                for (let i = 0; i < 6; i++) {
                    this.scene.remove(track.grids[n].borderMeshes[i]);
                    track.grids[n].borderMeshes[i].geometry.dispose();
                }
            }
        }

        if (track.drones) {

            for(let n in this.droneMeshCache){
                this.scene.remove(this.droneMeshCache[n].instanceMesh);
                this.droneMeshCache[n].instanceMesh.geometry.dispose();

                for (let pale of this.droneMeshCache[n].pales) {
                    this.scene.remove(pale.instanceMesh);
                    pale.instanceMesh.geometry.dispose();
                }
            }
            this.droneMeshCache = {};
        }

        if (track.springs) {
            for (let n = 0; n < track.springs.length; n++) {
                this.scene.remove(track.springs[n].obj.object);
                this.scene.remove(track.springs[n].anchor.object);

                track.springs[n].obj.object.geometry.dispose();
                track.springs[n].anchor.object.geometry.dispose();
            }
        }

        if (track.ropes) {
            for (let n = 0; n < track.ropes.length; n++) {
                this.scene.remove(track.ropes[n].obj.object);
                this.scene.remove(track.ropes[n].anchor.object);

                track.ropes[n].obj.object.geometry.dispose();
                track.ropes[n].anchor.object.geometry.dispose();
            }
        }
        this.scene.remove(this.startLine);

        track.woods = [];
        track.jumpers = [];
        track.grids = [];
        track.bonuses = [];
        track.springs = [];
        track.ropes = [];
        track.turrets = [];
        track.ropes = [];
        track.walls = [];
        track.roadObj = null;

    }

    initEngineUI() {

        const self = this;
        /*
        const ppi = document.createElement('input');
        ppi.className = "form-check-input";
        ppi.type = 'checkbox'
        ppi.id = "postprocessing";

        const ppl = document.createElement('label');
        ppl.className = "form-check-label";
        ppl.setAttribute('for', "postprocessing");
        ppl.innerHTML  = 'post processing';
        

        $('#engine-div').html('');
        $('#engine-div').append(ppi);
        $('#engine-div').append(ppl);
        */

        /*
        var myDefaultWhiteList = $.fn.tooltip.Constructor.Default.whiteList
        // To allow table elements
        myDefaultWhiteList.input = ['type']
        myDefaultWhiteList.label = ['for']
        */

        /*
        $('#engine-settings').popover({ html: true, content: '<input class="form-check-input" type="checkbox" id="raytracing"/><label id="raytracing" className="form-check-label" for="raytracing">raytracing</label><br/><input class="form-check-input" type="checkbox" id="postprocessing"/><label id="postprocessing" className="form-check-label" for="postprocessing">post processing</label><br/><input class="form-check-input" type="checkbox" id="parallax-mapping"/><label id="parallax-mappingl" className="form-check-label" for="parallax-mapping">parallax-mapping</label>' });

        $('#engine-settings').on('shown.bs.popover', function (e) {
            $('#postprocessing').val(1);
            $('#postprocessing').prop('checked', self.postProcessing);
            $('#postprocessing').on('change' , function(){ self.postProcessing=this.checked;  self.initPostProcessing(self.postProcessing); localStorage.setItem('postProcessing', this.checked?1:0); });


            $('#raytracing').val(1);
            $('#raytracing').prop('checked', self.enableRaytracing);
            $('#raytracing').on('change' , function(){ self.rayTracing=this.checked;  self.initRTReflections(self.rayTracing); localStorage.setItem('rayTracing', this.checked?1:0); });

            $('#parallax-mapping').val(1);
            $('#parallax-mapping').prop('checked', self.enableParallax);
            $('#parallax-mapping').on('change' , function(){ self.enableParallax = this.checked; localStorage.setItem('parallax', this.checked?1:0); if(self.enableParallax === true) { self.setBumpSize( self.bumpTexHeight, self.bumpTexWidth, self.driftSize); } });

        });
        */

    }

    printSkeleton(tskel) {


        tskel.update();


        for (let i = 0, il = tskel.bones.length; i < il; i++) {

            let p = new THREE.Vector3();
            let q = new THREE.Quaternion();
            let s = new THREE.Vector3();

            let ip = new THREE.Vector3();
            let iq = new THREE.Quaternion();
            let is = new THREE.Vector3();


            tskel.bones[i].matrixWorld.decompose(p, q, s);
            tskel.boneInverses[i].decompose(ip, iq, is);

            console.log('bone ' + tskel.bones[i].name + ' ' + p.x.toFixed(2) + ' ' + p.y.toFixed(2) + ' ' + p.z.toFixed(2));
            console.log('bone ' + tskel.bones[i].name + ' ' + q.x.toFixed(2) + ' ' + q.y.toFixed(2) + ' ' + q.z.toFixed(2) + ' ' + q.w.toFixed(2));

            console.log('ibone ' + tskel.bones[i].name + ' ' + ip.x.toFixed(2) + ' ' + ip.y.toFixed(2) + ' ' + ip.z.toFixed(2));
            console.log('ibone ' + tskel.bones[i].name + ' ' + iq.x.toFixed(2) + ' ' + iq.y.toFixed(2) + ' ' + iq.z.toFixed(2) + ' ' + iq.w.toFixed(2));


            let tp = new THREE.Vector3();
            let tq = new THREE.Quaternion();
            let ts = new THREE.Vector3();

            const m = new Matrix4();
            m.fromArray(tskel.boneMatrices, i * 16);

            m.decompose(tp, tq, ts);
            console.log('tbone ' + tskel.bones[i].name + ' ' + tp.x.toFixed(2) + ' ' + tp.y.toFixed(2) + ' ' + tp.z.toFixed(2));
            console.log('tbone ' + tskel.bones[i].name + ' ' + tq.x.toFixed(2) + ' ' + tq.y.toFixed(2) + ' ' + tq.z.toFixed(2) + ' ' + tq.w.toFixed(2));
        }
    }

    bindSkeleton(skeleton, mesh, size = 1) {

        const geo = this.doMeshSkinning(mesh.geometry, skeleton);
        const skinnedMesh = new THREE.SkinnedMesh(geo, mesh.material);
        geo.scale(size, size, size);

        const tskel = new THREE.Skeleton(skeleton.getThree(size));
        skinnedMesh.add(tskel.bones[0]);
        skinnedMesh.bind(tskel);

        return { skel: tskel, mesh: skinnedMesh };
    }

    resetAnimation(char) {

        if (!char.tskel)
            return;
        const map = char.skeleton.getBoneMap();

        for (let b in map) {
            if (b === 'root')
                continue;

            const bone = char.tskel.getBoneByName(b);
            if (bone) {
                const sb = char.skeleton.findBone(b);
                bone.quaternion.set(sb.quat.x, sb.quat.y, sb.quat.z, sb.quat.w);
            }
        }


    }

    applyAnimation(char, animation, t) {

        if (!animation)
            return;

        if (!char.tskel)
            return;

        for (let n = 0; n < animation.bones.length; n++) {

            const bone = char.tskel.getBoneByName(animation.bones[n].name);
            if (bone) {
                const q = animation.bones[n].getValue(t);
                bone.quaternion.set(q.x, q.y, q.z, q.w);
            }
        }

        //char.avatarMesh.updateMatrixWorld(true);

        /*
        if(char.avatarMesh.skeleton)
            this.printSkeleton(char.avatarMesh.skeleton);
        */
    }

    destroyMat(mat) {

        mat.dispose();
        if (mat.map)
            mat.map.dispose();

        if (mat.normalMap)
            mat.normalMap.dispose();

        if (mat.alphaMap)
            mat.alphaMap.dispose();

        if (mat.secMap)
            mat.secMap.dispose();

        if (mat.normalMap2)
            mat.normalMap2.dispose();

        if (mat.roughnessMap)
            mat.roughnessMap.dispose();

        if (mat.emissiveMap)
            mat.emissiveMap.dispose();

        if (mat.specularMap)
            mat.specularMap.dispose();
    }

    destroyLight(light) {
        light.light.dispose();
    }
    destroyObj(obj) {

        const self = this;

        obj.traverse(function (m) {

            if (m.isMesh) {
                m.geometry.dispose();

                if (!m.material)
                    return;

                if (m.material.length > 0) {
                    for (let mat of m.material) {
                        self.destroyMat(mat);
                    }
                } else {
                    self.destroyMat(m.material);
                }
            }
        });
    }

    destroyTrack(track) {
        if (track.roadObj !== null) {
            this.destroyObj(track.roadObj);
        }
        if (track.roadWall !== null) {
            this.destroyObj(track.roadWall);
        }
        for (let n = 0; n < track.walls.length; n++) {
            this.destroyObj(track.walls[n].object);
        }

        if (this.trackSpehres) {
            for (let n = 0; n < this.trackSpehres.length; n++) {
                this.destroyObj(this.trackSpehres[n]);
            }
        }

        if (track.woods) {
            for (let n = 0; n < track.woods.length; n++) {
                for (let i = 0; i < track.woods[n].objects.length; i++) {
                    this.destroyObj(track.woods[n].objects[i].object);
                }
            }
        }

        if (track.bonuses) {
            for (let n = 0; n < track.bonuses.length; n++) {
                this.destroyObj(track.bonuses[n].object);
                track.bonuses[n].light.dispose();
            }
        }

        if (track.jumpers) {
            for (let n = 0; n < track.jumpers.length; n++) {
                this.destroyObj(track.jumpers[n].object);

                if (track.jumpers[n].wireframe) {
                    this.destroyObj(track.jumpers[n].wireframe);
                }
            }
        }

        if (track.interestPoints) {

            for (let n = 0; n < track.interestPoints.length; n++) {
                this.destroyObj(track.interestPoints[n].object);

                if (track.interestPoints[n].wireframe) {
                    this.destroyObj(track.interestPoints[n].wireframe);
                }
            }
        }

        if (track.turrets) {
            for (let n = 0; n < track.turrets.length; n++) {
                this.destroyObj(track.turrets[n].object);
                this.destroyObj(track.turrets[n].canon);
                this.destroyObj(track.turrets[n].head);

                if (track.turrets[n].shotObject) {
                    this.destroyObj(track.turrets[n].shotObject);
                }
            }
        }

        if (track.drones) {

            for(let n in this.droneMeshCache){
                this.destroyObj(this.droneMeshCache[n].instanceMesh);

                for (let pale of this.droneMeshCache[n].pales) {
                    this.destroyObj(pale.instanceMesh);
                }
            }
            this.droneMeshCache={};

            for (let n = 0; n < track.drones.length; n++) {
                const drone = track.drones[n];
                for (let wing of drone.wings) {
                    this.destroyObj(wing);
                }
            }
        }


        if (track.springs) {
            for (let n = 0; n < track.springs.length; n++) {
                this.destroyObj(track.springs[n].obj.object);
                this.destroyObj(track.springs[n].anchor.object);
            }
        }

        if (track.ropes) {
            for (let n = 0; n < track.ropes.length; n++) {
                this.destroyObj(track.ropes[n].obj.object);
                this.destroyObj(track.ropes[n].anchor.object);
            }
        }

        for (let l of track.lights) {
            l.dispose();
        }

        for (let m of track.terrain.waterFlows) {
            this.destroyObj(m.object);
        }


        for (let m of track.terrain.movingTerrain) {
            this.destroyObj(m);
        }

        if (track.terrainGroup) {
            this.destroyObj(track.terrainGroup);
        }

        for (let it = 0; it < track.meshInstances.length; it++) {

            if (track.meshInstances[it].nFilled > 0)
                this.destroyObj(track.meshInstances[it].mesh);
        }

        for (let mc of track.terrain.cyls) {
            if (!mc.EngineMI) {
                this.destroyObj(mc);
            }
        }

        for (let mc of track.terrain.cubes) {
            if (!mc.EngineMI) {
                this.destroyObj(mc);
            }
        }

        for (let i = 0; i < track.xmeshes.length; i++) {
            if (track.xmeshes[i])
                this.destroyObj(track.xmeshes[i].mesh);
        }
    }




    removeTrack(track) {
        if (track.roadObj !== null) {
            this.scene.remove(track.roadObj);
        }

        if(this.toonGEO){
            this.toonGEO.dispose();
            this.toonGEO = null;
        }
        
        this.removeMesh(track.mesh1);
        this.removeMesh(track.mesh2);
        this.removeMesh(track.mesh3);
        this.removeMesh(track.mesh4);

        

        if(track.ground){
            if (this.rtEngine && (this.rtEngine.areas.length > 0)) {
                let area = null;
                while ((area = this.rtEngine.areas.pop())) {
                    this.removeMesh(area.mesh);
                }
            } else {
                 this.removeMesh(track.ground);
            }            
        }

        this.scene.remove(this.DMGBarsInstanceMesh);

        for (let grass of track.terrain.grasses)
            this.removeGrass(grass);

        for (let i = 0; i < track.wires.length; i++) {
            this.removeMesh(track.wires[i]);
        }

        for (let fa in this.fairies) {
            this.scene.remove(this.fairies[fa].hearth);
        }

        if (track.roadWall !== null) {
            this.scene.remove(track.roadWall);
        }
        for (let n = 0; n < track.walls.length; n++) {
            this.scene.remove(track.walls[n].object);
        }

        for (let n = 0; n < track.terrain.blinks.length; n++) {
            this.scene.remove(track.terrain.blinks[n].obj);
        }

        if (this.trackSpehres) {
            for (let n = 0; n < this.trackSpehres.length; n++) {
                this.scene.remove(this.trackSpehres[n]);
            }
        }

        for (let sp of track.terrain.lights) {
            this.scene.remove(sp.target);
            this.scene.remove(sp.light);
        }

        if (track.woods) {
            for (let n = 0; n < track.woods.length; n++) {
                for (let i = 0; i < track.woods[n].objects.length; i++) {
                    this.scene.remove(track.woods[n].objects[i].object);
                }
            }
        }

        if (track.bonuses) {
            for (let n = 0; n < track.bonuses.length; n++) {
                this.scene.remove(track.bonuses[n].object);
                this.scene.remove(track.bonuses[n].light);
            }
        }


        if (track.grids) {
            for (let n = 0; n < track.grids.length; n++) {
                this.scene.remove(track.grids[n].mesh);
                this.scene.remove(track.grids[n].fallBall.mesh);

                for (let i = 0; i < 6; i++) {
                    this.scene.remove(track.grids[n].borderMeshes[i]);
                }
            }
        }

        if (track.npcs) {
            for (let n = 0; n < track.npcs.length; n++) {
                this.scene.remove(track.npcs[n].avatarMesh);
                this.removeDialog(track.npcs[n]);

                if(this.rtEngine){
                    this.rtEngine.removeMesh(track.npcs[n].avatarMesh.rtMesh);
                }
            }
        }

        if (track.jumpers) {
            for (let n = 0; n < track.jumpers.length; n++) {
                this.scene.remove(track.jumpers[n].object);

                if (track.jumpers[n].wireframe) {
                    this.scene.remove(track.jumpers[n].wireframe);
                }
            }
        }

        if (track.interestPoints) {

            for (let n = 0; n < track.interestPoints.length; n++) {
                this.scene.remove(track.interestPoints[n].object);
                this.scene.remove(track.interestPoints[n].lmesh);

                if (track.interestPoints[n].wireframe) {
                    this.scene.remove(track.interestPoints[n].wireframe);
                }
            }
        }

        if (track.turrets) {
            for (let n = 0; n < track.turrets.length; n++) {
                this.scene.remove(track.turrets[n].object);
                this.scene.remove(track.turrets[n].head);

                let s = 0;
                while (s < track.turrets[n].samples.length) {

                    if (!track.turrets[n].samples[s].isPlaying) {
                        track.turrets[n].object.remove(track.turrets[n].samples[s]);
                        track.turrets[n].samples[s].pause();
                        track.turrets[n].samples[s].disconnect();
                        track.turrets[n].samples.splice(s, 1);
                    } else
                        s++;
                }


                for (let i = 0; i < track.turrets[n].shots.length; i++) {
                    this.removeSphere(track.turrets[n].shots[i]);

                    if(track.turrets[n].shots[i].particles)
                        this.removeParticles(track.turrets[n].shots[i].particles);
                    
                }

                if(track.turrets[n].shotSphere)
                    this.removeSphere(track.turrets[n].shotSphere);

                if(track.turrets[n].shotObject)
                    this.removeMesh(track.turrets[n].shotObject);

            }
        }

        
        if (track.spiders) {
            for (let n = 0; n < track.spiders.length; n++) {
                this.removeSpiderSkel(track.spiders[n].skel);

                this.removeSphere(track.spiders[n].shotSphere);

                let s = null;
                while((s  = track.spiders[n].samples.pop()) != null){
                    track.spiders[n].object.remove(s);
                    s.getOutput().disconnect();
                }
            }
        }
        if (track.drones) {

            for(let n in this.droneMeshCache){
                this.scene.remove(this.droneMeshCache[n].instanceMesh);
                for (let pale of this.droneMeshCache[n].pales) {
                    this.scene.remove(pale.instanceMesh);
                }
            }


            for (let n = 0; n < track.drones.length; n++) {
                this.scene.remove(track.drones[n].positionalAudio);
                
                for (let i = 0; i < track.drones[n].shots.length; i++) {

                    if(track.drones[n].weapons[0].shotSphere)
                        this.removeSphere(track.drones[n].shots[i]);

                    if(track.drones[n].weapons[0].shotObject)
                        this.removeMesh(track.drones[n].shots[i]);
                }

                for(let w of track.drones[n].weapons){
                    
                    if(w.shotSphere)
                        this.removeSphere(w.shotSphere);

                    if(w.shotObject)
                        this.removeMesh(w.shotObject);
                }
            }
        }

        if (track.springs) {
            for (let n = 0; n < track.springs.length; n++) {
                this.scene.remove(track.springs[n].obj.object);
                this.scene.remove(track.springs[n].anchor.object);
            }
        }

        if (track.ropes) {
            for (let n = 0; n < track.ropes.length; n++) {
                this.scene.remove(track.ropes[n].obj.object);
                this.scene.remove(track.ropes[n].anchor.object);
            }
        }
        this.scene.remove(this.startLine);

        for (let l of track.lights) {
            this.scene.remove(l);
        }

        for (let m of track.terrain.movingTerrain) {
            this.scene.remove(m);
        }
        for (let m of track.terrain.waterFlows) {
            this.scene.remove(m.object);
            m.object.remove(m.positionalAudio);
        }


        if (track.terrainGroup) {
            this.scene.remove(track.terrainGroup);
        }

        if (track.terrain.mesh)
            this.scene.remove(track.terrain.mesh);

        if (track.terrain.ground)
            this.scene.remove(track.terrain.ground);


        if (track.ground)
            this.scene.remove(track.ground);

        if (this.targetMesh) {
            this.scene.remove(this.targetMesh);
        }

        if (this.toonMESH) {
            this.scene.remove(this.toonMESH);
        }

        for (let it = 0; it < track.meshInstances.length; it++) {
            this.scene.remove(track.meshInstances[it].mesh);
        }

        for (let mc of track.terrain.cyls) {
            if (!mc.EngineMI) {
                this.scene.remove(mc);
            }
        }

        for (let mc of track.terrain.cubes) {
            if (!mc.EngineMI) {
                this.scene.remove(mc);
            }
        }

        for (let i = 0; i < track.xmeshes.length; i++) {
            if (track.xmeshes[i])
                this.scene.remove(track.xmeshes[i].mesh);
        }
    }

    addMeshGroup(m) {
        this.scene.add(m);
    }

    createGPUTextureFromImge(bitmap, is8bit = false, flip = true) {
        return new THREE.Texture(bitmap, THREE.Texture.DEFAULT_MAPPING, THREE.RepeatWrapping, THREE.RepeatWrapping, THREE.LinearFilter, THREE.LinearFilter, THREE.RedFormat, THREE.UnsignedByteType, 1, THREE.NoColorSpace);
    }

    createGPUDataTexture(width, height, data) {

        const newTex = new THREE.DataTexture(data, width, height,
            THREE.RedFormat,
            THREE.UnsignedByteType,
            THREE.Texture.DEFAULT_MAPPING,
            THREE.RepeatWrapping,
            THREE.RepeatWrapping,
            THREE.LinearFilter,
            THREE.LinearFilter,
            1,
            THREE.NoColorSpace);

        newTex.flipY = false;
        newTex.generateMipmaps = false;

        return newTex; 
    }

    createTextureCanvas(canvas, flip = true) {

        const t = new THREE.Texture(canvas);
        t.flipY = flip;
        t.isCanvas = true;
        return t;
    }

    fixEmm(c) {

        if (c.material.length) {
            for (let m of c.material) {
                if ((m.emissive.r > 0) || (m.emissive.g > 0) || (m.emissive.b > 0)) {
                    m.toneMapped = false;
                    //m.emissive.g= Math.max(0.1, m.emissive.g)
                    //m.emissiveIntensity = 8.0;
                    /*console.log(m.name);*/
                }
            }
        } else {
            const m = c.material;
            if ((m.emissive) && ((m.emissive.r > 0) || (m.emissive.b > 0) || (m.emissive.g > 0))) {
                m.toneMapped = false;
                //m.emissive.g= Math.max(0.1, m.emissive.g)
                //m.emissiveIntensity = 8.0;
                //console.log(m.name);
            }

        }
    }

    createTrackInfos(track) {

        const curve = track.curveC;

        const geo = new THREE.BufferGeometry();
        const mat = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, depthTest: false });

        const position = new Float32Array(curve.points.length * 2);
        const up = new THREE.Vector3(0, 1, 0);

        for (var i = 0; i < curve.points.length; i++) {

            const ot = curve.getTangent(i / curve.points.length);
            const t = ot.cross(up);


            const p = curve.getPoint(i / curve.points.length);
            const ll = t.clone().multiplyScalar(-5).add(p);
            const lr = t.clone().multiplyScalar(5).add(p);

            position[(i * 2 + 0) * 3 + 0] = ll.x;
            position[(i * 2 + 0) * 3 + 1] = ll.y;
            position[(i * 2 + 0) * 3 + 2] = ll.z;

            position[(i * 2 + 1) * 3 + 0] = lr.x;
            position[(i * 2 + 1) * 3 + 1] = lr.y;
            position[(i * 2 + 1) * 3 + 2] = lr.z;
        }

        geo.setAttribute('position', new THREE.Float32BufferAttribute(position, 3));

        const ls = new THREE.LineSegments(geo, mat);
        this.scene.add(ls);
    }

    removeGrass(grass) {
        this.scene.remove(grass.mesh);
    }

    getMeshPos(mesh, id) {

        mesh.getMatrixAt(id, this.tmpM)
        return new THREE.Vector3(this.tmpM[13], this.tmpM[14], this.tmpM[15]);
    }

    getMeshRot(mesh, id) {

        mesh.getMatrixAt(id, this.tmpM);
        return new THREE.Quaternion().setFromRotationMatrix(this.tmpM);
    }

    addGrass(grass) {

        grass.mat = new THREE.MeshStandardMaterial().copy(grass.mat);

        grass.mesh = new THREE.InstancedMesh(grass.geo, grass.mat, grass.nGrass);
        grass.mesh.name = 'grass';
        grass.mesh.castShadow = true;
        grass.scale = new THREE.Vector3(1, 1, 1);
        const m = new THREE.Matrix4();

        if(grass.mat.transparent){
            grass.mat.alphaTest = 0.3;
            grass.mat.depthWrite = true;
            grass.mat.depthTest = true;
            grass.mat.opacity = 0.99;
        }

        for (let n = 0; n < grass.nGrass; n++) {
            m.compose(grass.transforms[n].position, grass.transforms[n].quaternion, grass.scale);
            grass.mesh.setMatrixAt(n, m);
        }

        this.scene.add(grass.mesh);
    }
    dumpBones(bone, imat) {

        const tmpM = imat.clone().multiply(bone.matrixWorld);

        const q = new THREE.Quaternion().setFromRotationMatrix(tmpM);

        console.log('bone' + bone.name + ' ' + tmpM.elements[12].toFixed(2) + ' ' + tmpM.elements[13].toFixed(2) + ' ' + tmpM.elements[14].toFixed(2) + ' ' + q.x.toFixed(2) + ' ' + q.y.toFixed(2) + ' ' + q.z.toFixed(2) + ' ' + q.w.toFixed(2));

        for (let b of bone.children) {
            this.dumpBones(b, imat);
        }

    }
    addMeshInstance(m, type, t) {

        const newObj = m.clone();
        newObj.position.set(t.position.x, t.position.y, t.position.z);
        newObj.quaternion.set(t.quaternion.x, t.quaternion.y, t.quaternion.z, t.quaternion.w);
        this.scene.add(newObj);

        return newObj;

    }

    addRTMesh(mc, lname, meshInstances, transform = null) {
        let n = null;

        for (let it of meshInstances) {
            if (lname.startsWith(it.key)) {
                n = it.name;
                break;
            }
        }

        if ((n === null) || (!this.rtEngine.meshInstances[n])) {
            mc.rtMesh = this.rtEngine.addMesh(mc, RT_COAT, transform !== null ? transform : new Transformation3(mc.position, mc.quaternion));
            this.rtEngine.drawMesh(mc.rtMesh);

            if (n !== null)
                this.rtEngine.meshInstances[n] = mc.rtMesh;

        } else {
            mc.rtMesh = this.rtEngine.addMeshInstance(this.rtEngine.meshInstances[n], transform !== null ? transform : new Transformation3(mc.position, mc.quaternion));
            mc.rtMesh.name = mc.name;
            this.rtEngine.drawMesh(mc.rtMesh);

            console.log(lname + ' as instance of ' + n);
        }
    }


    splitBone(splitName, bone, tbone, relMatrix = null) {
        
        let newBone = null;
        
        const currentMatrix = (relMatrix === null) ? tbone.matrix.clone() : new THREE.Matrix4().copy(relMatrix).multiply(tbone.matrix);

        for(let i =0; i<bone.children.length; i++) {

            const child = bone.children[i];
            const tchild = tbone.children[i];

            if(child.name === splitName){
                  
                newBone =  new SimpleBone(child.name, new THREE.Quaternion(), child.length, child.radius, child.jradius, child.color.clone());
                newBone.copy(child);

                newBone.parentName = bone.name;
                
                newBone.pos = new THREE.Vector3();
             
                return newBone;
            }
            
            newBone = this.splitBone(splitName, child, tchild, currentMatrix);
            if(newBone != null)
                break;
        }
        return newBone;
    }


    splitBoneMesh(boneName, char){

        const newRootBone = this.splitBone(boneName, char.skeleton.root, char.tskel.bones[0]);
        this.resetSkel(newRootBone);

        const ids = this.getIds(newRootBone);
        const geo = char.avatarMesh.geometry;

        const newMesh = char.avatarMesh.clone();
        newMesh.geometry = geo.clone();
        newMesh.children = [];

     
        //const tbone = char.tskel.getBoneByName(newRootBone.name);

        for(let n=0;n<char.tskel.bones.length;n++){
            
            if(char.tskel.bones[n].name == newRootBone.name){

                //newMesh.splitQuat = new THREE.Quaternion();
                //char.tskel.boneInverses[n].decompose(this.v1, newMesh.splitQuat, this.tmpVec3);

                newMesh.geometry.applyMatrix4(char.tskel.boneInverses[n]);
              
                newMesh.geometry.computeBoundingBox();
                break;
            }
        }
        

        const newIndexes1 = new Uint16Array(geo.index.count);
        const newIndexes2 = new Uint16Array(geo.index.count);

        let nIdx1 = 0;
        let nIdx2 = 0;

        for(let i =0; i<geo.index.count; i+=3){

            let hasBoneVtx = -1;
                       
            for(let v = 0;v<3;v++) {

                const idx = geo.index.array[i + v];

                for(let b = 0;b<4;b++) { 

                    if(ids.indexOf(geo.attributes.skinIndex.array[ idx * 4 + b])>=0){
                        hasBoneVtx= geo.attributes.skinIndex.array[ idx * 4 + b];
                        break;
                    }
                }

                if(hasBoneVtx >= 0)
                    break;
            }

            if(hasBoneVtx >= 0){
        
                for(let v = 0;v<3;v++) {

                    const vidx = geo.index.array[i + v];

                    for(let n=0;n<4;n++){
                        
                        if(newMesh.geometry.attributes.skinWeight.array[vidx * 4 + n] > 0){
                           newMesh.geometry.attributes.skinIndex.array[vidx * 4 + n] = Math.max(0.0, geo.attributes.skinIndex.array[vidx * 4 + n] - newRootBone.idx);
                        }
                            
                    }
                    
                    newIndexes2[nIdx2++] = vidx;
                }
            }else{
                for(let v = 0;v<3;v++) {
                    newIndexes1[nIdx1++] = geo.index.array[i + v];
                }
            }
        }


        geo.setIndex(new THREE.Uint16BufferAttribute(newIndexes1, 1));
        char.avatarMesh.geometry.drawRange.count = nIdx1;

        newMesh.geometry.setIndex(new THREE.Uint16BufferAttribute(newIndexes2, 1));
        newMesh.geometry.drawRange.count = nIdx2;


        newMesh.position.set(0, 0, 0);
        newMesh.quaternion.set(0, 0, 0, 1.0);
        newMesh.updateMatrixWorld(true);

        const newSkeleton = new SimpleSkeleton(new THREE.Vector3(), newRootBone.quat);
        newSkeleton.root.children = newRootBone.children;
        newSkeleton.root.idx = newRootBone.idx;
        newSkeleton.root.id = newRootBone.id;

        const tskel = new THREE.Skeleton(newSkeleton.getThree(char.skelScale));
        newMesh.add(tskel.bones[0]);
        newMesh.bind(tskel);

        char.splitMesh.push({ skel : newSkeleton, mesh : newMesh});
        this.addMesh(newMesh);

        return newMesh;
    }

    async addTrack(track) {

        this.scene.add(this.targetMesh);
        
        this.maxDroneGeoIdx = 0;
        this.maxDroneGeoVtx = 0;

        if (track.roadObj) {
            track.roadObj.castShadow = false;
            track.roadObj.receiveShadow = true;
            track.roadObj.renderOrder = 11;

            track.walls[0].object.castShadow = true;
            track.walls[0].object.receiveShadow = true;
            track.walls[0].object.material.envMap = this.skyTexture;
            track.walls[0].object.material.needsUpdate = true;
            this.addMesh(track.walls[0].object);

            if(this.rtEngine){
                track.walls[0].object.rtMesh = this.rtEngine.addMesh(track.walls[0].object, RT_DIFF, new Transformation3(track.walls[0].object.position, track.walls[0].object.quaternion));
                this.rtEngine.drawMesh(track.walls[0].object.rtMesh);
                track.walls[0].object.material.uIgnoreGeoID = track.walls[0].object.rtMesh.geoID;
            }
            
            for (let n = 1; n < track.walls.length; n++) {

                track.walls[n].object = this.addMeshInstance(track.walls[0].object, 0, track.walls[n].object);

                if(this.rtEngine){
                    track.walls[n].object.rtMesh = this.rtEngine.addMeshInstance(track.walls[0].object.rtMesh, new Transformation3(track.walls[n].object.position, track.walls[n].object.quaternion));
                    this.rtEngine.drawMesh(track.walls[n].object.rtMesh);
                    track.walls[n].object.material.uIgnoreGeoID = track.walls[n].object.rtMesh.geoID;
                }
                //this.scene.add(track.walls[n].wall);
            }
        }

        /*
        for(let n=0;n<track.loopings.length;n++){
            const c= new THREE.Vector3();
            const s= new THREE.Vector3();

            track.loopings[n].bbox.getCenter(c);
            track.loopings[n].bbox.getSize(s);

            const box = new THREE.BoxGeometry(s.x, s.y, s.z);
            const m = new THREE.Mesh(box);
            m.position.set(c.x, c.y, c.z);
            this.scene.add(m);
        }
        */


        if (track.roadWall) {
            track.roadWall.castShadow = false;
            track.roadWall.receiveShadow = true;
            track.roadObj.renderOrder = 10;

            if(this.rtEngine){
                track.roadWall.rtMesh = this.rtEngine.addMesh(track.roadWall, RT_DIFF, new Transformation3(track.roadWall.position, track.roadWall.quaternion));
                this.rtEngine.drawMesh(track.roadWall.rtMesh);
                
                track.roadWall.material.uIgnoreGeoID = track.roadWall.rtMesh.geoID;
            }
        }


        if ((track.woodDefs) && (track.woodDefs.length > 0)) {
            for (let n = 0; n < track.woodDefs.length; n++) {
                const wood = track.addWoods(track.woodDefs[n].ptIdx, track.woodDefs[n].num, track.woodDefs[n].width, track.woodDefs[n].radius, track.woodDefs[n].mass, track.woodDefs[n].offset, track.woodDefs[n].spacing);
                this.addTrackWoods(wood);
            }
        }


        if ((track.springDefs) && (track.springDefs.length > 0)) {
            for (let n = 0; n < track.springDefs.length; n++) {
                let platform;

                if (track.springDefs[n].model.length > 0) {
                    const gltf = await this.loadGLTF(track.springDefs[n].model);
                    platform = this.deGroupGLB(gltf.children[0]);
                    platform = platform.children[0];
                } else {
                    platform = await this.loadObj('plateforme');
                    platform = platform.children[0];
                    platform.scale.set(track.springDefs[n].size.x, track.springDefs[n].size.y, track.springDefs[n].size.z);
                }

                const spring = track.addSpring(track.springDefs[n].ptIdx, track.springDefs[n].offset, track.springDefs[n].eqPts, track.springDefs[n].eqA, track.springDefs[n].mass, track.springDefs[n].size, track.springDefs[n].stiffness, track.springDefs[n].damping, track.springDefs[n].initPts, track.springDefs[n].initA, track.springDefs[n].limit, track.springDefs[n].limitA, track.springDefs[n].shape, track.springDefs[n].mode, track.springDefs[n].model);

                this.addTrackSpring(spring, platform);

                //track.terrain.movingTerrain.push(spring.obj.object);

                if (this.rtEngine) {
                    spring.obj.object.rtMesh = this.rtEngine.addMesh(spring.obj.object, RT_COAT, new Transformation3(spring.obj.object.position, spring.obj.object.quaternion));
                    this.rtEngine.drawMesh(spring.obj.object.rtMesh);
                }

            }
        }


        if ((track.gridDefs) && (track.gridDefs.length > 0)) {
            for (let n = 0; n < track.gridDefs.length; n++) {
                const grid = track.addGrid(track.gridDefs[n].ptIdx, track.gridDefs[n].offset, track.gridDefs[n].angle, track.gridDefs[n].cell_size, track.gridDefs[n].width, track.gridDefs[n].height, track.gridDefs[n].depth, track.gridDefs[n].isolevel, track.gridDefs[n].nBalls, track.gridDefs[n].ballRadius, track.gridDefs[n].ballWeight);
                this.addTrackGrid(grid);
            }
        }

        if ((track.spiderDefs) && (track.spiderDefs.length > 0)) {

            for (let n = 0; n < track.spiderDefs.length; n++) {
                const spider = track.addSpider(track.spiderDefs[n].ptIdx, track.spiderDefs[n].offset, track.spiderDefs[n].angle, track.spiderDefs[n].size, track.spiderDefs[n].model, track.spiderDefs[n].shotmodel, track.spiderDefs[n].projSize, track.spiderDefs[n].projMass, track.spiderDefs[n].projSpeed, track.spiderDefs[n].projDelay, track.spiderDefs[n].activation);
                await this.addTrackSpider(spider);
            }

        }

        if ((track.ropeDefs) && (track.ropeDefs.length > 0)) {
            for (let n = 0; n < track.ropeDefs.length; n++) {
                const rope = track.addRope(track.ropeDefs[n].ptIdx, track.ropeDefs[n].offset, track.ropeDefs[n].eqPts, track.ropeDefs[n].eqA, track.ropeDefs[n].mass, track.ropeDefs[n].size, track.ropeDefs[n].softness, track.ropeDefs[n].damping, track.ropeDefs[n].motorforce, track.ropeDefs[n].bounce, track.ropeDefs[n].initPts, track.ropeDefs[n].initA, track.ropeDefs[n].limit, track.ropeDefs[n].limitA, track.ropeDefs[n].shape, track.ropeDefs[n].anchorObj, track.ropeDefs[n].model);
                track.computeRope(rope);

                let obj = this.plateform;
                if (rope.model) {

                    /*
                    const mod = await this.loadObj(rope.model);
                    mod.traverse(function(child){
                        if(child.isMesh)
                            obj = child;
                    });
                    */

                    const gltf = await this.loadGLTF(rope.model);
                    obj = this.deGroupGLB(gltf.children[0]);
                    obj = obj.children[0];
                }

                this.addTrackRope(rope, obj);

            }
        }

        if (!track.bonusObject) {

            const name = "bonus";
            track.bonusObject = await this.loadObj(name);
        }

        if (!track.techObject) {

            const name = "tech";
            track.techObject = await this.loadObj(name);
        }

        if ((track.bonusDefs) && (track.bonusDefs.length > 0)) {
            for (let n = 0; n < track.bonusDefs.length; n++) {
                const bonus = track.addBonus(track.bonusDefs[n].ptIdx, track.bonusDefs[n].type, track.bonusDefs[n].amount, track.bonusDefs[n].offset, track.bonusDefs[n].size);
                let obj;
                switch (track.bonusDefs[n].type) {
                    case 0: obj = track.bonusObject; break;
                    case 1:
                        switch (track.bonusDefs[n].amount) {
                            case 1: obj = await this.loadObj("battery_bp"); break;
                            case 2: obj = await this.loadObj("radar_bp"); break;
                            case 3: obj = await this.loadObj("weapon_bp"); break;
                        }
                        break;
                    case 2: obj = track.techObject; break;
                }
                this.addTrackbonus(bonus, obj);
            }
        }

        for (let n = 0; n < track.loopings.length; n++) {
            for (let i = 0; i < track.loopings[n].bonuses.length; i++) {
                const bonus = track.addBonus(track.loopings[n].bonuses[i].ptIdx, track.loopings[n].bonuses[i].type, track.loopings[n].bonuses[i].amount, track.loopings[n].bonuses[i].offset, track.loopings[n].bonuses[i].size);
                this.addTrackbonus(bonus, track.bonusObject);
            }
        }

        if ((track.interestPointsDefs) && (track.interestPointsDefs.length > 0)) {
            const name = "ip";

            const object = await this.loadObj(name);

            for (let n = 0; n < track.interestPointsDefs.length; n++) {

                const ip = await track.addInterestPoint(track.interestPointsDefs[n].ptIdx,
                    track.interestPointsDefs[n].offset,
                    track.interestPointsDefs[n].angle,
                    track.interestPointsDefs[n].size,
                    track.interestPointsDefs[n].lobject,
                    track.interestPointsDefs[n].loffset,
                    track.interestPointsDefs[n].lsize,
                    track.interestPointsDefs[n].lweight,
                    track.interestPointsDefs[n].nBonus,
                    track.interestPointsDefs[n].targetNode);

                await this.makeInterestPoint(ip, object);

                //ip.object.geometry.applyMatrix4(ip.object.matrix);

                this.scene.add(ip.object);
                this.scene.add(ip.lmesh);
            }
        }

        if (track.charDefs) {
            for (let n = 0; n < track.charDefs.length; n++) {
                const char = await track.addNPC(track.charDefs[n].ptIdx, track.charDefs[n].offset, track.charDefs[n].angle, track.charDefs[n].name, track.charDefs[n].subsystemsDef, track.charDefs[n].skelScale, track.charDefs[n].nInstances);
            }

            for (let n = 0; n < track.npcs.length; n++) {

                await this.makeCharacter(track.npcs[n]);

                if(track.npcs[n].instanceID>0){
                    const iof = track.npcs[track.npcs[n].instanceOf];

                    if(track.npcs[n].skeleton){
                        track.npcs[n].avatarMesh = iof.originalMesh.clone();
                        track.npcs[n].avatarMesh.rtMesh = iof.avatarMesh.rtMesh;
                    }
                    else
                        track.npcs[n].avatarMesh = iof.avatarMesh;
                    
                    track.npcs[n].center = iof.center.clone();
                    track.npcs[n].size = iof.size.clone();
                }

                if ((track.npcs[n].instanceID == 0) || (track.npcs[n].skeleton))
                    this.addCharObj(track.npcs[n]);
                else {
                    
                    //track.npcs[n].avatarMesh = track.npcs[track.npcs[n].instanceOf].avatarMesh;

                    track.npcs[n].target = { id: this.targetID++, eid : track.npcs[n].id,  type: 'npc', name: 'npc-' + track.npcs[n].id, subsystems: [], pos: { x: track.npcs[n].pos.x, y: track.npcs[n].pos.y + (track.npcs[n].avatarDef.height * track.npcs[n].skelScale) / 2 + track.npcs[n].worldOffset.y * 2, z: track.npcs[n].pos.z } };

                    if (this.rtEngine) {
                        track.npcs[n].avatarMesh.rtMesh[track.npcs[n].instanceID] = this.rtEngine.addMeshInstance( track.npcs[n].avatarMesh.rtMesh[0], new Transformation3(track.npcs[n].pos, track.npcs[n].rot));
                        this.rtEngine.drawMesh(track.npcs[n].avatarMesh.rtMesh[track.npcs[n].instanceID]);
                        this.rtEngine.alwaysRender.push(track.npcs[n].avatarMesh.rtMesh[track.npcs[n].instanceID].geoID);
                    }
                }

                this.createSelectMesh(track.npcs[n]);
            }

         
        }

        if ((track.droneDefs) && (track.droneDefs.length > 0)) {

            let totalGeos = 0;

            for (let n = 0; n < track.droneDefs.length; n++) {
                const drone = await track.addDrone(track.droneDefs[n].ptIdx, track.droneDefs[n].offset, track.droneDefs[n].caroffset, track.droneDefs[n].angle, track.droneDefs[n].size, track.droneDefs[n].model, track.droneDefs[n].activation, track.droneDefs[n].deactivation, track.droneDefs[n].speed, track.droneDefs[n].weight, track.droneDefs[n].weapons, track.droneDefs[n].speedPale1, track.droneDefs[n].speedPale2, track.droneDefs[n].upLift, track.droneDefs[n].pales);
                await this.makeDrone(drone);
                totalGeos += 1 + drone.pales.length;
            }

            //this.droneBatch = new THREE.BatchedMesh(totalGeos, this.maxDroneGeoVtx, this.maxDroneGeoIdx, this.droneMaterialCache[0] );

            for (let n = 0; n < track.drones.length; n++) {
                this.addTrackDrone(track.drones[n]);
            }

            //this.scene.add(this.droneBatch);
        }

        /*
        for (let n = 0; n < track.interestPointsDefs.length; n++) {

            const ip = await track.addInterestPoint(track.interestPointsDefs[n].ptIdx, track.interestPointsDefs[n].offset, track.interestPointsDefs[n].angle, track.interestPointsDefs[n].size, track.interestPointsDefs[n].lobject, track.interestPointsDefs[n].loffset, track.interestPointsDefs[n].object);

            await this.makeInterestPoint(ip);
    
            this.scene.add(ip.object);
            this.scene.add(ip.lmesh);
        }
        */

        if ((track.jumpersDefs) && (track.jumpersDefs.length > 0)) {
            const name = "jumper";
            const object = await this.loadObj(name);

            for (let n = 0; n < track.jumpersDefs.length; n++) {
                const jumper = await track.addJumper(track.jumpersDefs[n].ptIdx, track.jumpersDefs[n].offset, track.jumpersDefs[n].angle, track.jumpersDefs[n].size);
                await this.addTrackJumper(jumper, object);
            }
        }


        if ((track.turretsDefs) && (track.turretsDefs.length > 0)) {

            for (let n = 0; n < track.turretsDefs.length; n++) {
                const turret = await track.addTurret(track.turretsDefs[n].ptIdx, track.turretsDefs[n].model, track.turretsDefs[n].shotModel, track.turretsDefs[n].activation, track.turretsDefs[n].offset, track.turretsDefs[n].angle, track.turretsDefs[n].size, track.turretsDefs[n].projPts, track.turretsDefs[n].projSize, track.turretsDefs[n].projMass, track.turretsDefs[n].projSpeed, track.turretsDefs[n].projAngle, track.turretsDefs[n].projAngle2, track.turretsDefs[n].projDelay, track.turretsDefs[n].projTTL, track.turretsDefs[n].projSND, track.turretsDefs[n].projLL, track.turretsDefs[n].moveAngle, track.turretsDefs[n].projRoll, track.turretsDefs[n].projPart);
                await this.addTrackTurret(turret);
            }
        }


        let grp = new THREE.Group();
        const self = this;

        const uniqueGeoMats = {};

        const gltf = await this.loadGLTF('hearth');
        const h = this.deGroupGLB(gltf);
        const hearth = h.children[0];

        hearth.material[0].blending = THREE.AdditiveBlending;
        hearth.material[0].opacity = 0.8;
        hearth.material[0].transparent = true;

        if (this.rtEngine) {
            hearth.rtMesh = this.rtEngine.addMesh(hearth);
        }


        const m = new THREE.Matrix4();
        track.terrain.objs = [];
        track.terrain.mesh.traverse(child => {

            if (!child.isMesh)
                return;

            child.position.add(track.terrain.offset).multiplyScalar(track.gscale);
            child.geometry.scale(track.gscale, track.gscale, track.gscale);

            this.fixEmm(child);

            if ((child.name.startsWith('Wheel')) ||
                (child.name.startsWith('wire')) ||
                (child.name.startsWith('Crystal')) ||
                (child.name.startsWith('buddha')) ||
                (child.name.startsWith('Star')) ||
                (child.name.startsWith('SWheel')) ||
                (child.name.startsWith('fire-start'))) {

                const ar = child.name.split('-');

                const o = child.clone();

                if ((ar.length >= 2) && (ar[1].startsWith('Dirigeable')))
                    o.castShadow = false;

                if (child.name.startsWith('SWheel'))
                    o.oQuaternion = o.quaternion.clone();

                if ((ar.length >= 2) && ((ar[1].startsWith('torus')) || (ar[1].startsWith('fan'))))
                    o.castShadow = false;

                if ((ar.length >= 2) && (ar[1].startsWith('fairy'))) {

                    const maxHearth = 40;


                    const hearthInstances = new THREE.InstancedMesh(hearth.geometry, hearth.material, maxHearth);

                    this.scene.add(hearthInstances);

                    this.fairies[ar[1]] = { hearth: hearthInstances, fairy: o, lastTime: 0, cnt: 0, maxHearth: maxHearth, rtObjs: [] };

                    m.identity();
                    m.scale(0, 0, 0);

                    for (let n = 0; n < maxHearth; n++) {
                        hearthInstances.setMatrixAt(n, m);

                        if (this.rtEngine) {

                            const mt = new Transformation3();
                            mt.fromMat4(m);
                            this.fairies[ar[1]].rtObjs[n] = { rtMesh: this.rtEngine.addMeshInstance(hearth.rtMesh, mt) };
                            this.rtEngine.drawMesh(this.fairies[ar[1]].rtObjs[n].rtMesh);
                        }
                    }

                    for (let fm = 0; fm < o.material.length; fm++) {

                        if (o.material[fm].name === 'Toon-fairytail') {
                            const c = self.getGeoGrpCenter(o.geometry, fm);
                            this.fairies[ar[1]].center = c.c;
                        }
                    }
                }

                o.speeds = [ar.length > 2 && ar[2].length>0 ? parseFloat(ar[2]) / 10.0 : 1];

                if (o.material.length > 0) {

                    for (let imat = 0; imat < o.material.length; imat++) {
                        let mat = o.material[imat];

                        if (mat.name.startsWith('Toon')) {
                            mat = this.setToon(mat);
                            o.material[imat] = mat;

                            const ng = this.addFlippedGeo(o.geometry, o.geometry.groups[imat]);
                            const toonMesh = new THREE.Mesh(ng, this.toonFlippedMat);
                            toonMesh.name = o.name +' '+mat.name+' toon mesh';
                            o.add(toonMesh);
                        }
                    }
                }

                track.terrain.movingTerrain.push(o);

            } else if (track.movingObjects.hasOwnProperty(child.name)) {

                const o = child.clone();
                //o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);

                if (o.name == 'seagull-yf') {

                    const audioLoader = new THREE.AudioLoader();
                    track.movingObjects[o.name].positionalAudio = new THREE.PositionalAudio(this.AudioListener);

                    audioLoader.load(this.assetBase+'/assets/snd/seagull.mp3', function (buffer) {
                        track.movingObjects[o.name].positionalAudio.setBuffer(buffer);
                        track.movingObjects[o.name].positionalAudio.setDistanceModel('exponential');
                        track.movingObjects[o.name].positionalAudio.setRolloffFactor(1.5);
                        track.movingObjects[o.name].positionalAudio.setRefDistance(2);
                        track.movingObjects[o.name].positionalAudio.setLoop(true);

                        o.add(track.movingObjects[o.name].positionalAudio);
                    });
                }

                track.terrain.movingTerrain.push(o);

            } else if (child.name.startsWith('path')) {
            } else if (child.name.startsWith('Anchor')) {
                /*
                const o = child.clone();
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);

                track.terrain.movingTerrain.push(o);
                */

            } else if (child.name.startsWith('Axis')) {
                /*
                const o = child.clone();
                o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                track.terrain.movingTerrain.push(o);
                */

            } else if (child.name.startsWith('Area')) {
            } else if (child.name.startsWith('rt-lamp')) {

                const center = new THREE.Vector3();
                const size = new THREE.Vector3();

                c.geometry.computeBoundingBox();
                c.geometry.boundingBox.getCenter(center);
                c.geometry.boundingBox.getSize(size);


                const q = new THREE.Quaternion();
                const c = center;//.add(track.terrain.offset);

                const newcyl = {};
                this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), 0.6, RT_REFR);

                const newsphere = {};
                const r = Math.random();
                const col = track.terrain.cyls.length & 1 ? new THREE.Vector3(1, 0.25, 0.25) : new THREE.Vector3(0.25, 0.8, 0.25)

                /*
                this.addSphere(newsphere,
                    4,
                    new THREE.Vector3(c.x, c.y + size.y / 2.0 + 3, c.z),
                    col.multiplyScalar(0.8),
                    col,
                    0.2,
                    RT_POINT_LIGHT
                );
                */

                track.terrain.cyls.push({ radiusX: size.x / 2.0, radiusZ: size.z / 2.0, b: new THREE.Vector3(c.x, c.y - size.y / 2, c.z), height: size.y, isCyl: true, mesh: newcyl, l: newsphere });

            } else if (child.name.startsWith('rt-cylinder')) {

                child.geometry.computeBoundingBox();
                const box = child.geometry.boundingBox;
                const q = new THREE.Quaternion();
                const center = new THREE.Vector3();
                const size = new THREE.Vector3();
                box.getCenter(center);
                box.getSize(size);

                const c = center;//.add(track.terrain.offset);
                const newcyl = {};
                const type = child.material.name.startsWith('Water') ? RT_REFR : RT_COAT;

                this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), child.material.roughness, type);

                track.terrain.cyls.push({ radiusX: size.x / 2.0, radiusZ: size.z / 2.0, b: new THREE.Vector3(c.x, c.y - size.y / 2, c.z), height: size.y, isCyl: true, mesh: newcyl, l: null });

            } else if (child.name.startsWith('col-barrel')) {

                const mc = child.clone();

                mc.geometry.computeBoundingSphere();

                const c = mc.geometry.boundingSphere.center.clone()

                mc.pos = { x: mc.position.x + c.x, y: mc.position.y + c.y, z: mc.position.z + c.z };

                mc.geometry.translate(-c.x, -c.y, -c.z);
                mc.position.set(mc.pos.x, mc.pos.y, mc.pos.z);

                mc.castShadow = true;


                mc.matrixAutoUpdate = false;
                mc.updateMatrix();
                mc.updateWorldMatrix(true);
                mc.updateMatrixWorld(true);
                //mc.matrixWorldAutoUpdate = false;

                mc.isCyl = false;

                const lname = child.name.substr(11);
                const it = track.findInstance(lname);

                if (it) {

                    if (!it.mesh) {
                        it.mesh = new THREE.InstancedMesh(mc.geometry, mc.material, it.nInstances);
                        it.mesh.name = 'instances-'+lname;
                        this.addMesh(it.mesh);
                    }

                    mc.EngineMI = { it: it, idx: it.nFilled };
                    it.mesh.setMatrixAt(it.nFilled++, mc.matrix);
                } else {
                    mc.meshGPU = this.addMesh(mc, RT_COAT);
                    this.setMeshStatic(mc, false);
                }

                //this.addCylinder(newcyl, size.x / 2.0, c, size.y, q, new THREE.Vector3(child.material.color.r, child.material.color.g, child.material.color.b), 0.6, RT_COAT);
                track.terrain.cyls.push(mc);

                if (this.rtEngine) {
                    this.addRTMesh(mc, lname, track.meshInstances);
                }
            } else if (child.name.startsWith('lamp')) {

                const mc = child.clone();
                const c = new THREE.Vector3();
                mc.geometry.computeBoundingBox();
                mc.geometry.boundingBox.getCenter(c);

                const size = new THREE.Vector3();
                for (let n = 0; n < mc.material.length; n++) {

                    if (mc.material[n].name === 'lampcol') {
                        const cc = this.getGeoGrpCenter(mc.geometry, n);
                        c.set(cc.c.x, cc.c.y, cc.c.z);
                        //size.set(cc.s.x,cc.s.y, cc.s.z);
                    }
                }


                mc.pos = { x: mc.position.x + c.x, y: mc.position.y + c.y , z: mc.position.z + c.z };
                mc.geometry.translate(-c.x, -c.y, -c.z);

                for (let n = 0; n < mc.material.length; n++) {

                    if (mc.material[n].name.startsWith('Toon')) {
                        mc.material[n] = this.setToon(mc.material[n]);

                        const ng = this.addFlippedGeo(mc.geometry, mc.geometry.groups[n]);
                        const toonMesh = new THREE.Mesh(ng, this.toonFlippedMat);
                        toonMesh.name = 'ToonMesh';
                        mc.add(toonMesh);
                    }
                }
                mc.position.set(mc.pos.x, mc.pos.y, mc.pos.z);

                mc.matrixAutoUpdate = false;
                mc.updateMatrix();
                mc.updateWorldMatrix(true);
                mc.updateMatrixWorld(true);
                //mc.matrixWorldAutoUpdate = false;

                mc.isCyl = false;

                const it = track.findInstance('lamp');

                if (it) {

                    if (!it.mesh) {
                        it.mesh = new THREE.InstancedMesh(mc.geometry, mc.material, it.nInstances);
                        it.mesh.name = 'instances-lamp';
                        this.addMesh(it.mesh);
                    }

                    mc.EngineMI = { it: it, idx: it.nFilled };
                    it.mesh.setMatrixAt(it.nFilled++, mc.matrix);
                } else {
                    mc.meshGPU = this.addMesh(mc, RT_COAT);
                    this.setMeshStatic(mc, false);
                }

                track.terrain.cyls.push(mc);
                if (this.rtEngine) {
                    this.addRTMesh(mc, 'lamp', track.meshInstances);

                }

            } else if (child.name.startsWith('col-cube') || child.name.startsWith('col-cyl')) {

                const lname = child.name.startsWith('col-cube') ? child.name.substr(9) : child.name.substr(8);

                if (!lname.startsWith('invisible')) {

                    if (lname.startsWith('sign')) {
                        const o = child.clone();
                        o.name = lname;
                        track.terrain.movingTerrain.push(o);
                    } else {
                        this.addUniqueMaterial(child, uniqueGeoMats);
                    }

                    if (this.rtEngine) {
                        this.addRTMesh(child, lname, track.meshInstances);
                    }
                }

            } else if (child.name.startsWith('arch')) {

                track.terrain.movingTerrain.push(child.clone());


            } else if ((child.name.startsWith('trigger')) || (child.name.startsWith('power'))) {

                const ar = child.name.split('-');

                child.material = child.material.clone();
                child.material.name = 'trigger-' + ar[1];

                this.addUniqueMaterial(child, uniqueGeoMats);

            } else if (child.name.startsWith('col-dcube')) {

                const mc = child.clone();
                

                mc.geometry.computeBoundingSphere();

                const c = mc.geometry.boundingSphere.center;

                mc.opos = { x: c.x + mc.position.x, 
                            y: c.y + mc.position.y, 
                            z: c.z + mc.position.z };

                mc.geometry.translate(-c.x, -c.y, -c.z);
                mc.position.set(mc.opos.x, mc.opos.y, mc.opos.z);

                if (child.material.length > 0) {

                } else if (child.material.name === 'fence') {
                    child.material.alphaTest = 0.2;
                    child.material.transparent = true;
                }
                mc.isCube = false;

                mc.matrixAutoUpdate = false;
                mc.updateMatrix();
                mc.updateWorldMatrix(true);
                mc.updateMatrixWorld(true);

                const lname = mc.name.substr(10);
                const it = track.findInstance(lname);

                if (it) {

                    if (!it.mesh) {
                        it.mesh = new THREE.InstancedMesh(mc.geometry, mc.material, it.nInstances);
                        it.mesh.name= 'instances-'+lname;
                        
                        this.addMesh(it.mesh);
                    }

                    mc.EngineMI = { it: it, idx: it.nFilled };
                    it.mesh.setMatrixAt(it.nFilled++, mc.matrix);
                } else {
                    this.addMesh(mc, RT_COAT);
                    this.setMeshStatic(mc, false);
                }

                track.terrain.cubes.push(mc);

                if (this.rtEngine) {
                    this.addRTMesh(mc, lname, track.meshInstances);
                }


            } else if (child.name.startsWith('rt-sphere')) {

                /*
                child.geometry.computeBoundingSphere();
                const sphere = child.geometry.boundingSphere;
                const newsphere = {};
                
                const q = new THREE.Quaternion();
                const center = sphere.center.clone().add(track.terrain.offset);
                const size = new THREE.Vector3();
                
             
                this.addSphere(newsphere,
                    sphere.radius,
                    new THREE.Vector3(center.x, center.y, center.z),
                    new THREE.Vector3(child.material.emissive.r, child.material.emissive.g, child.material.emissive.b).multiplyScalar(8),
                    new THREE.Vector3(child.material.color.r, child.material.color.g,child.material.color.b),
                    0.2,
                    RT_POINT_LIGHT
                );

                track.terrain.spheres.push(newsphere);
                */
            } else if (child.SimpleSkeleton) {

                const o = child;

                self.removeGeoGrp(child, ['center', 'bone', 'ccube', 'Anchor']);

                //o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                track.terrain.movingTerrain.push(o);
            } else if (child.name === 'Plane') {

                const o = child.clone();
                //o.position.set(o.position.x + track.terrain.offset.x, o.position.y + track.terrain.offset.y, o.position.z + track.terrain.offset.z);
                //this.scene.add(o);


                if (track.trackPoints.length > 1) {

                    //this.addUniqueMaterial(child, uniqueGeoMats, track.terrain.offset);
                    track.ground = o;
                    track.ground.castShadow = false;
                    track.ground.renderOrder = 16000;
                    this.scene.add(o);

                } else {
                    track.ground = o;

                    if (this.rtEngine) {
                        track.ground.rtMesh = this.rtEngine.addMesh(track.ground, RT_COAT, new Transformation3(track.ground.position, track.ground.quaternion), false); //                    this.scene.add(o);
                        this.rtEngine.drawMesh(track.ground.rtMesh);
                    }
                }
                track.ground.geometry.computeBoundsTree();

            } else if ((child.name.startsWith('cascade')) || (child.name.startsWith('island'))) {


                const water = child.clone();
                //water.position.set(water.position.x + track.terrain.offset.x, water.position.y + track.terrain.offset.y, water.position.z + track.terrain.offset.z);

                for (let mi = 0; mi < water.material.length; mi++) {

                    let m = water.material[mi];

                    if (m.name == 'waterflow') {
                        /*
                        m.opacity = 1.0;
                        m.transmission = 1.0;
                        m.dispersion = 0.1;
                        m.transparent = false;   
                        m.thickness = 0.02;
                        */
                        m.ior = 1.4;
                        m.roughness = 0.0;
                        m.reflectivity = 0.8;

                        /*
                        m.emissiveMap = null;
                        m.emissive.setRGB(0,0,0);
                        m.emissiveIntensity = 0.0;
                        */
                    } else {

                        if (m.name.startsWith('Toon')) {
                            water.material[mi] = this.setToon(m);

                            const ng = this.addFlippedGeo(water.geometry, water.geometry.groups[mi]);
                            const newm = new THREE.Mesh(ng, this.toonFlippedMat);
                            water.add(newm);
                        }
                    }
                }
                const audioLoader = new THREE.AudioLoader();
                const positionalAudio = new THREE.PositionalAudio(this.AudioListener);

                const wat = { object: water, positionalAudio: positionalAudio };

                audioLoader.load(this.assetBase+'/assets/snd/waterflow.ogg', function (buffer) {
                    wat.positionalAudio.setBuffer(buffer);
                    wat.positionalAudio.setDistanceModel('exponential');
                    wat.positionalAudio.setRolloffFactor(1.5);
                    wat.positionalAudio.setRefDistance(15);
                    wat.object.add(wat.positionalAudio);
                });


                track.terrain.waterFlows.push(wat);
                this.scene.add(wat.object);

                if (this.rtEngine) {
                    wat.object.rtMesh = this.rtEngine.addMesh(wat.object, RT_COAT, new Transformation3(wat.object.position, wat.object.quaternion));
                    this.rtEngine.drawMesh(wat.object.rtMesh);
                }



            } else if (child.name.startsWith('zcrane')) {
                //child.geometry.translate(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z);

                for (let mat of child.material) {
                    if (mat.transparent) {
                        mat.alphaTest = 0.3;
                    }
                }

                //grp.add(child.clone());
                this.addUniqueMaterial(child, uniqueGeoMats);

            } else if (child.name.startsWith('light')) {

                const col = child.material.color;
                const sp = {};

                const dir = new THREE.Vector3(0, 0, 1).applyQuaternion(child.quaternion).multiplyScalar(100.0);

                sp.light = new THREE.SpotLight(col.getHex(), 1.0, 50, Math.PI / 3, 0.2, 0.4);
                sp.light.position.set(child.position.x, child.position.y, child.position.z);
                //sp.light.position.add(track.terrain.offset);

                sp.geom = new THREE.BoxGeometry(0.001, 0.001, 0.001, 1, 1, 1);
                sp.target = new THREE.Mesh(sp.geom, this.SphereMaterial);
                sp.target.position.addVectors(sp.light.position, dir);
                sp.target.name= 'level light target';
                sp.light.name= 'level light';

                sp.light.target = sp.target;
                this.scene.add(sp.target);
                this.scene.add(sp.light);

                track.terrain.lights.push(sp);


            } else if (child.name.startsWith('blink')) {

                const c = child.clone();
                const arr = c.name.split('-');
                const name = arr[1];
                const timeFac = parseFloat(arr[2]) / 10.0;
                //c.position.add(track.terrain.offset);

                let nMats = 0;

                for (let ms = 0; ms < child.material.length; ms++) {

                    if (child.material[ms].name.startsWith(name)) {
                        const a = child.material[ms].name.split('-');
                        const n = parseInt(a[1]);
                        nMats = Math.max(nMats, n);
                    }
                }

                track.terrain.blinks.push({ obj: c, mats: name, nMats: nMats, timeFac: timeFac });
                this.scene.add(c);

            } else if (child.name.startsWith('xmesh')) {

                const o = child.clone();
                o.name = o.name.substring(6);

                if(o.material.length>0) {
                    o.geometry.groups[o.geometry.groups.length] = { start: o.geometry.groups[o.geometry.groups.length - 1].start + o.geometry.groups[o.geometry.groups.length - 1].count, count: 0, materialIndex: o.material.length };
                    o.material[o.material.length] = this.splitMaterial;

                    for (let ms = 0; ms < o.material.length; ms++) {
                        o.material[ms].side = THREE.DoubleSide;
                    }
                } else {
                    o.geometry.groups[0] = { start: 0                       , count: o.geometry.index.count , materialIndex: 0 };
                    o.geometry.groups[1] = { start: o.geometry.index.count  , count: 0                      , materialIndex: 1 };
                    o.material[0] = o.material;
                    o.material[1] = this.splitMaterial;

                    o.material[0].side = THREE.DoubleSide;
                    o.material[1].side = THREE.DoubleSide;
                }
                const newXMesh = { id: track.lastId++, splitted: false, name: o.name, mass: 0, size: null, pos: o.position.clone(), mesh: o, rot: child.quaternion.clone(), nVtxAlloc: 128 * 3, nVtx: 0, meshBox: null, boxGeo: null, meshArray: null, vertices: null, normals: null, uvs: null };
                newXMesh.ogeo = o.geometry.clone();
                o.castShadow = true;
                track.computeXMesh(newXMesh);
                track.xmeshes.push(newXMesh);

            } else if (child.name.startsWith('Grass')) {


            } else {
                /*
                child.geometry.translate(track.terrain.offset.x, track.terrain.offset.y, track.terrain.offset.z);
                const c = child.clone();
                grp.add(c);
                track.terrain.objs.push(c);
                */
                const lname = this.getInstName(child.name);
                /*
                const it = track.findInstance(lname);
                if (it) {
                    
                    if (!it.mesh) {
                        it.mesh = new THREE.InstancedMesh(child.geometry, child.material, it.nInstances);
                        this.addMesh(it.mesh);
                    }

                    child.EngineMI = { it: it, idx: it.nFilled };
                    child.updateMatrix();

                    const matrix = child.matrix.clone();
                    it.mesh.setMatrixAt(it.nFilled++, matrix);


                } else*/
                {
                    this.addUniqueMaterial(child, uniqueGeoMats);
                }


                if (this.rtEngine) {
                    //const pos = new THREE.Vector3().addVectors(child.position, track.terrain.offset);
                    this.addRTMesh(child, lname, track.meshInstances,  new Transformation3(child.position, child.quaternion));
                }
                
            }
        });


        /*
      for(let i =0; i< track.terrain.waterFlows.length;i++)
      {
          this.scene.add(track.terrain.waterFlows[i].object);

      }
          */

        for (let n = 0; n < track.npcs.length; n++) {
            for (let s of track.npcs[n].target.subsystems) {
                this.addDMGBar(s.dmgBar);
            }
        }

        for (let n = 0; n < track.turrets.length; n++) {
            this.addDMGBar(track.turrets[n].dmgBar);
        }        

        for (let n=0; n<track.drones.length;n++) {
            for (let p=0; p<track.drones[n].pales.length;p++) {
                this.addDMGBar(track.drones[n].pales[p].dmgBar);
            }
        }

        for(let n=0;n<track.roadGrasses.length;n++){
            const grass = this.generateGrass(track, track.roadGrasses[n].num, track.roadGrasses[n].width, track.roadGrasses[n].offset, track.roadGrasses[n].size, track.roadGrasses[n].img);
            track.terrain.grasses.push(grass);
        }

        
        for (let grass of track.terrain.grasses)
            this.addGrass(grass);
        
        
        for (let i = 0; i < track.xmeshes.length; i++) {
            this.addMesh(track.xmeshes[i].mesh);
            //this.addMesh(track.xmeshes[i].mnn);

            if (this.rtEngine) {
                track.xmeshes[i].mesh.rtMesh = this.rtEngine.addMesh(track.xmeshes[i].mesh, RT_COAT, new Transformation3(track.xmeshes[i].mesh.position, track.xmeshes[i].mesh.quaternion));
                this.rtEngine.drawMesh(track.xmeshes[i].mesh.rtMesh);
            }
        }


        for (let i = 0; i < track.terrain.movingTerrain.length; i++) {
            if (track.terrain.movingTerrain[i].SimpleSkeleton) {
                const tskel = new THREE.Skeleton(track.terrain.movingTerrain[i].SimpleSkeleton.getThree());
                const skinnedMesh = new THREE.SkinnedMesh(track.terrain.movingTerrain[i].geometry, track.terrain.movingTerrain[i].material);
                skinnedMesh.name = track.terrain.movingTerrain[i].name;
                skinnedMesh.add(tskel.bones[0]);
                skinnedMesh.bind(tskel);
                skinnedMesh.position.set(track.terrain.movingTerrain[i].position.x, track.terrain.movingTerrain[i].position.y, track.terrain.movingTerrain[i].position.z);
                skinnedMesh.quaternion.set(track.terrain.movingTerrain[i].quaternion.x, track.terrain.movingTerrain[i].quaternion.y, track.terrain.movingTerrain[i].quaternion.z, track.terrain.movingTerrain[i].quaternion.w);
                track.terrain.movingTerrain[i] = skinnedMesh;
            }

            this.addMesh(track.terrain.movingTerrain[i]);

            if (this.rtEngine) {
                track.terrain.movingTerrain[i].rtMesh = this.rtEngine.addMesh(track.terrain.movingTerrain[i], RT_COAT, new Transformation3(track.terrain.movingTerrain[i].position, track.terrain.movingTerrain[i].quaternion));
                this.rtEngine.drawMesh(track.terrain.movingTerrain[i].rtMesh);
            }
        }

        for (let i = 0; i < track.wires.length; i++) {
            this.addMesh(track.wires[i]);

            if (this.rtEngine) {
                track.wires[i].rtMesh = this.rtEngine.addMesh(track.wires[i], RT_COAT, new Transformation3(track.wires[i].position, track.wires[i].quaternion));
                this.rtEngine.drawMesh(track.wires[i].rtMesh);
            }

        }


        const tubcol = new THREE.Color().setRGB(track.tubecolor.r, track.tubecolor.g, track.tubecolor.b);
        this.tubemat = new THREE.MeshPhongMaterial({ color: tubcol.getHex(), emissive: tubcol.getHex(), toneMapped: false });
        this.tubemat.name = "tubemat";
        this.tubemat.emissiveIntensity = 8.0;
        this.tubemat.toneMapped = false;

        //this.wallMat.color.setRGB(track.tubecolor.r, track.tubecolor.g, track.tubecolor.b);

        const newGeos1 = [];
        const newMats1 = [];
        const attrs = [];



        if (track.trackPoints.length > 1) {
            const keys= Object.keys(uniqueGeoMats);

            const hasIndex = uniqueGeoMats.length > 0 ? uniqueGeoMats[keys[0]].geos[0].index ? true: false : true;

            const tubeGeometry1 = new THREE.TubeGeometry(track.curveL, track.NPoints, 0.25, 4, true);
            tubeGeometry1.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry1.tangents, 4));
            track.mesh1 = new THREE.Mesh(hasIndex ? tubeGeometry1 : tubeGeometry1.toNonIndexed(), this.tubemat);

            const tubeGeometry3 = new THREE.TubeGeometry(track.curveLU, track.NPoints, 0.25, 4, true);
            tubeGeometry3.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry3.tangents, 4));
            track.mesh3 = new THREE.Mesh(hasIndex ? tubeGeometry3 : tubeGeometry3.toNonIndexed(), this.tubemat);

            const tubeGeometry2 = new THREE.TubeGeometry(track.curveR, track.NPoints, 0.25, 4, true);
            tubeGeometry2.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry2.tangents, 4));
            track.mesh2 = new THREE.Mesh(hasIndex ? tubeGeometry2 : tubeGeometry2.toNonIndexed(), this.tubemat);

            const tubeGeometry4 = new THREE.TubeGeometry(track.curveRU, track.NPoints, 0.25, 4, true);
            tubeGeometry4.setAttribute('tangent', new THREE.Float32BufferAttribute(tubeGeometry4.tangents, 4));
            track.mesh4 = new THREE.Mesh(hasIndex ? tubeGeometry4 : tubeGeometry4.toNonIndexed(), this.tubemat);

            //mesh1.material.name="rt_coat";

            //this.addUniqueMaterial(mesh1, uniqueGeoMats);
            //this.addUniqueMaterial(mesh2, uniqueGeoMats);
            //this.addUniqueMaterial(mesh3, uniqueGeoMats);
            //this.addUniqueMaterial(mesh4, uniqueGeoMats);

            
            this.addMesh(track.mesh1);
            this.addMesh(track.mesh2);
            this.addMesh(track.mesh3);
            this.addMesh(track.mesh4);

             if (this.rtEngine) {
                track.mesh1.rtMesh = this.rtEngine.addMesh(track.mesh1, RT_COAT, new Transformation3(track.mesh1.position, track.mesh1.quaternion));
                this.rtEngine.drawMesh(track.mesh1.rtMesh);

                track.mesh2.rtMesh = this.rtEngine.addMesh(track.mesh2, RT_COAT, new Transformation3(track.mesh2.position, track.mesh2.quaternion));
                this.rtEngine.drawMesh(track.mesh2.rtMesh);

                track.mesh3.rtMesh = this.rtEngine.addMesh(track.mesh3, RT_COAT, new Transformation3(track.mesh3.position, track.mesh3.quaternion));
                this.rtEngine.drawMesh(track.mesh3.rtMesh);

                track.mesh4.rtMesh = this.rtEngine.addMesh(track.mesh4, RT_COAT, new Transformation3(track.mesh4.position, track.mesh4.quaternion));
                this.rtEngine.drawMesh(track.mesh4.rtMesh);
            }
            
        }
       
        for (let um in uniqueGeoMats) {

            uniqueGeoMats[um].geo = mergeGeometries(uniqueGeoMats[um].geos);
            for(let g of uniqueGeoMats[um].geos){
                g.dispose();
            }

            uniqueGeoMats[um].geos = null;

            const g = uniqueGeoMats[um].geo;
            const m = uniqueGeoMats[um].mat;

            newGeos1.push(g);
            newMats1.push(m);
        }


        let rtGPU = null;
        if (this.rtEngine) {

            for (let n = 0; n < track.terrain.blinks.length; n++) {

                const child = track.terrain.blinks[n].obj;
                const lname = child.name.substr(6);
                this.addRTMesh(child, lname, track.meshInstances);
            }

        }

        if (this.rtEngine) {

            const excl = [];

            if (track.ground.rtMesh)
                excl.push(track.ground.rtMesh.geoID);

            this.rtEngine.buildAreas(excl);

            /*
            if(track.roadObj){
                track.roadObj.rtMesh = this.rtEngine.addMesh(track.roadObj, RT_DIFF, new Transformation3(track.roadObj.position, track.roadObj.quaternion));
                this.rtEngine.drawMesh(track.roadObj.rtMesh);
                track.roadObj.material.uIgnoreGeoID = track.roadObj.rtMesh.geoID;
            }
            */
        }

        if (newGeos1.length > 0) {
            const newgeo1 = mergeGeometries(newGeos1, true);

            for (let at in newgeo1.attributes) {
                attrs.push(newgeo1.attributes[at]);
            }

            const iattr = interleaveAttributes(attrs);
            let ia = 0;
            for (let at in newgeo1.attributes) {
                newgeo1.attributes[at] = iattr[ia++];
            }

            for(let matid =0;matid<newMats1.length;matid++){

                this.resizeMaterial(newMats1[matid]);

                if((!newMats1[matid].roughnessMap)&&(!newMats1[matid].normalMap)){

                    if(!newMats1[matid].name.startsWith('Toon'))
                        newMats1[matid]= new THREE.MeshPhongMaterial().copy(newMats1[matid]);
                }

            }

            const newmesh1 = new THREE.Mesh(newgeo1, newMats1);
            newmesh1.name = "static-background"
            newmesh1.castShadow = true;
            newmesh1.receiveShadow = true;
            newmesh1.rtMesh = rtGPU;

            track.terrainGroup = newmesh1;
        } else
            track.terrainGroup = new THREE.Group();

        if (this.toonGEO) {

            this.toonMESH = new THREE.Mesh(this.toonGEO, this.toonFlippedMat);
            this.toonMESH.name = 'toon mesh';
            this.addMesh(this.toonMESH);
        }

        if (track.trackPoints.length > 1)
            this.scene.add(this.startLine);

        this.addMesh(track.terrainGroup);
        this.addMesh(track.ground);

        if(track.roadObj){
            
            if(this.rtEngine)
                this.rtEngine.enableRT(track.roadObj);

            this.scene.add(track.roadObj);
        }

        if(track.roadWall){

            if(this.rtEngine)
                this.rtEngine.enableRT(track.roadWall);

            track.roadWall.geometry.computeBoundingBox();
            this.scene.add(track.roadWall);                
        }

    }

    enableTrackRT(world) {

        if (!this.rtEngine)
            return;

        for (let n = 0; n < world.track.grids.length; n++) {
            this.rtEngine.enableRT(world.track.grids[n].mesh);
        }


        //this.rtEngine.enableRT(world.myCar.ChassisMesh);

        
        if (world.track.trackPoints.length <= 1) {

            if (this.rtEngine.areas.length > 0) {
                this.removeMesh(world.track.ground);
                this.rtEngine.buildMesh(world.track.ground);

                for (let area of this.rtEngine.areas) {
                    this.addMesh(area.mesh);
                }
            } else {
                this.rtEngine.enableRT(world.track.ground);
            }

            const tex = [];
            for (let n = 0; n < this.rtEngine.BigTextures.length; n++) {
                tex.push(this.rtEngine.BigTextures[n].tex);
            }

            for (let m = 0; m < world.track.terrainGroup.material.length; m++) {

                const mat = world.track.terrainGroup.material[m];
                if (mat.name.startsWith('bridgeroad')) {

                    const nm = new THREE.MeshRTPhysicalMaterial({ envMapIntensity: 0.35 });
                    nm.copy(mat);
                    this.rtEngine.enableMatRT(nm, tex);
                    world.track.terrainGroup.material[m] = nm;

                    /*
                    const c = this.getGeoGrpCenter(world.track.terrainGroup.geometry, m);
                    const center = new THREE.Vector3(world.track.terrainGroup.position.x, world.track.terrainGroup.position.y, world.track.terrainGroup.position.z).add(c.c);
                    world.track.terrainGroup.material[m].rtGeos = this.rtEngine.findGeos(center, 800);
                    */

                    //this.rtEngine.enableRT(world.track.roadWall);
                }
            }
        }
    }

    resizeCanvasToDisplaySize(width, height) {
        const canvas = this.renderer.domElement;
        // look up the size the canvas is being displayed
        const dw = width;
        const dh = height; //width >= height ? height : width

        // adjust displayBuffer size to match
        if (canvas.width !== dw || canvas.height !== dh) {
            // you must pass false here or three.js sadly fights the browser

            this.renderer.setSize(dw, dh);

            if (this.composer)
                this.composer.setSize(dw, dh);

            if(this.MBPass)
                this.MBPass.setSize(dw, dh);
            


            this.camera.aspect = dw / dh;

            //this.camera = new THREE.PerspectiveCamera( 75, width / height, 0.1, 20000 );
            this.camera.updateProjectionMatrix();
            // update any render target sizes here
        }
    }

    getCharBone(skel, name) {
        return skel.getBoneByName(name);
    }

    interpolateRagDoll(skel, bone, lerper, time) {

        const mybone = this.getCharBone(skel, bone.name);
        if (mybone) {
            if (lerper != null) {

                lerper.getInterpolatedValue(bone.idx, time, mybone);

                //mybone.quaternion.w = mybone.quaternion.w;

                /*
                if(mode === true)
                    mybone.quaternion.w = -mybone.quaternion.w;
                */

                bone.life = mybone.life;
            } else {

                if (bone.pos)
                    mybone.position.set(bone.pos.x, bone.pos.y, bone.pos.z);

                mybone.quaternion.set(bone.rot.x, bone.rot.y, bone.rot.z, bone.rot.w);
            }
        }

        for (let n = 0; n < bone.children.length; n++) {
            this.interpolateRagDoll(skel, bone.children[n], lerper, time);
        }

    }

    setupCarMat(mat) {
        /*
        switch (this.MatType) {
            default:
            case 0:

                //this.envmap = await this.generator.fromEquirectangular(this.skyTexture).texture;
                mat.envMapIntensity = 0.4;
                //mat.envMap = await this.generator.fromEquirectangular(this.skyTexture).texture;
                mat.envMap = this.skyTexture;
                mat.needsUpdate = true;
                break;
            case 1:
                mat.envMapIntensity = 0.8;
                mat.metalness = 0.4;
                mat.bumpScale = 0.05;
                mat.reflectivity = 0.1;
                mat.sheen = 0;
                mat.sheenRoughness = 0.0;
                mat.needsUpdate = true;
                break;
        }
        */


    }


    postRender(world) {

        if (this.skyTexture == null)
            return;

        if (this.skyTexture.image == null)
            return;

        if (this.sc)
            return;


        if (world.allCarsLoaded()) {

            if ((world.cars.length == 0) && (world.myCar != null)) {
                if (world.myCar.ChassisMesh) {
                    if (world.myCar.ChassisMesh.material.length) {
                        for (let mat of world.myCar.ChassisMesh.material) {
                            this.setupCarMat(mat);
                        }
                    } else {
                        this.setupCarMat(world.myCar.ChassisMesh.material);
                    }
                }

            } else {
                for (let car of world.cars) {
                    //const envmap = this.generator.fromEquirectangular(this.skyTexture);
                    if (car.ChassisMesh.material.length) {
                        for (let mat of car.ChassisMesh.material) {
                            this.setupCarMat(mat);
                        }
                    } else {
                        this.setupCarMat(car.ChassisMesh.material);
                    }
                }

            }


            if ((world.track) && (world.track.terrain) && (world.track.terrain.mesh)) {

                world.track.terrain.mesh.traverse((child) => {



                    if (child.name.startsWith('arch')) {
                        child.material.transmission = 1.0;
                        child.material.thickness = 0.2;
                        child.material.roughness = 0.0;
                        child.material.reflectivity = 0.2;
                    }

                    if (child.name.startsWith('tube')) {
                        child.material[0].transmission = 0.8;
                        child.material[0].opacity = 1.0;
                        child.material[0].transparent = false;
                        child.material[0].thickness = 0.2;
                        child.material[0].roughness = 0.8;
                        child.material[0].reflectivity = 0.2;
                        child.material[0].envMap = this.skyTexture;
                        child.material[0].envMapIntensity = 2;
                        child.material[0].ior = 1.6;
                    }

                   
                    /*
                    if (child.type == 'Mesh') {

                        if (child.material.opacity < 1.0) {
                            child.material.envMap = this.skyTexture;
                            child.material.envMapIntensity = 8;
                            child.material.metalness = 1.0;
                            child.material.needsUpdate = true;

                        }
                    }
                    */
                });
            }

            const self = this;

            //setTimeout(function () { self.initPostProcessing(self.postProcessing); }, 1000);

            this.sc = true;
        }
    }

    setBloomParams(strength, radius, threshold) {
        this.bloom.strength = strength;
        this.bloom.radius = radius;
        this.bloom.threshold = threshold;

        if (this.RBPass) {
            this.RBPass.strength = this.bloom.strength;
            this.RBPass.radius = this.bloom.radius;
            this.RBPass.threshold = this.bloom.threshold;
        }
    }

    initPostProcessing(enable) {



        if (enable === false) {
            this.composer = null;
            this.renderer.setRenderTarget(null);
            this.scene.onAfterRender = function () { }
            this.renderer.xr.setComposer(null);

            return;
        } else if ((this.composer !== null)) {


            if (this.isVr)
                this.renderer.xr.setComposer(this.composer.readBuffer);


            return;
        }

        this.composer = new EffectComposer(this.renderer);

        if(this.isVR) {
            this.renderer.setRenderTarget(this.composer.readBuffer);
        }else {
            const renderPass = new RenderPass(this.scene, this.camera);
            this.composer.addPass(renderPass);
        }



        /*
        const ssaoPASS = new SSAOPass( this.scene, this.camera);
        ssaoPASS.kernelRadius = 4;
        ssaoPASS.minDistance = 0.001;
        ssaoPASS.maxDistance = 0.3;
        ssaoPASS.kernelSize = 16;
        this.composer.addPass( ssaoPASS );
        */
        

        /*
        const saopass = new SAOPass(this.scene, this.camera, false, false, new THREE.Vector2(1024, 1024));

        saopass.params = {
            output: 0,
            saoBias: 0.01,
            saoIntensity: 0.01,
            saoScale: 4,
            saoKernelRadius: 40,
            saoMinResolution: 0,
            saoBlur: true,
            saoBlurRadius: 4,
            saoBlurStdDev: 1.2,
            saoBlurDepthCutoff: 0.1
        };

        this.composer.addPass(saopass);
        */


        /*
        const outline=[];
        this.scene.traverse(function (child){

            if(!child.isMesh)
                return;

            if(child.material.length) {

                for(let n=0; n<child.material.length; n++) {

                    if(child.material[n].isMeshToonMaterial )
                        outline.push(child);
                }
            }else{
                if(child.material.isMeshToonMaterial )
                    outline.push(child);

            }
        })
        
        const OLPass = new OutlinePass({X : 1024, y: 1024}, this.scene, this.camera, outline);
        
        var params = {
            edgeStrength: 20,
            edgeGlow: 1,
            edgeThickness: 10.0,
            pulsePeriod: 0,
            usePatternTexture: false
        };
        OLPass.renderToScreen = true;
        OLPass.selectedObjects = outline;
        OLPass.edgeStrength = params.edgeStrength;
        OLPass.edgeGlow = params.edgeGlow;
        OLPass.visibleEdgeColor.set(0xffffff);
        OLPass.hiddenEdgeColor.set(0xffffff);

        this.composer.addPass( OLPass );
        */




        /*
        const bokehPass = new BokehPass(this.scene, this.camera, {
            focus: 1.0,
            aperture: 0.15,
            maxblur: 0.4
        });

        this.composer.renderToScreen = true;

        this.composer.addPass(bokehPass);
        */

        this.MBScene = new THREE.Scene();

        /*
        this.MBPass = new MotionBlurPass(this.scene, this.camera, { blueNoiseTex: this.blueNoiseTex});
        this.MBPass.renderToScreen = true;
        this.composer.addPass(this.MBPass);
        */


        this.RBPass = new UnrealBloomPass({ X: 1024, y: 1024 }, this.bloom.strength, this.bloom.radius, this.bloom.threshold);
        this.composer.addPass(this.RBPass);

        const self = this;

        if(this.isVR) 
        {
            this.scene.onAfterRender = function () {

                if (self.composer)
                    self.composer.render();

            };
        }
        
        this.renderer.compile(this.scene, this.camera);


        /*
        this.scene.onAfterRender = function () {

            if(self.composer)
                self.composer.render();

        };
        */
        /*
        const glitchPass = new GlitchPass();
        this.composer.addPass( glitchPass );
        */
        /*
        const RBPass = new UnrealBloomPass({X : 1024, y: 1024}, 0.45, 1, 0.5);
        this.composer.addPass( RBPass );
        */




        //const rPass = new RenderPass( this.sceneNOPP, this.camera );
        //this.composer.addPass( rPass );


        //const noppPass = new RenderPass( this.sceneNOPP, this.camera );
        //this.composer.addPass( noppPass );

        /*
        const renderUIPass = new RenderPass( this.sceneUI, this.cameraUI);
        this.composer.addPass( renderUIPass );
        */
    }


    render(dt) {

        /*
        this.cameraTransform.set(this.camera.position, this.camera.quaternion);
        cameraTransform.getInverse(this.vt);
        */

        
        let n = 0;
        while (n < this.particles.length) {

            const p = this.particles[n];
            p.update(dt);
            if ((!p.emitterAlive) && (!p.pinned) && (p.aliveParticles < 1)) {
                this.scene.remove(p.particleMesh);
                this.particles.splice(n, 1);
            } else
                n++
        }
        
       
        if (!this.isVr) {
            this.renderer.alpha = true;
            this.renderer.preserveDrawingBuffer = false;
            this.renderer.autoClearDepth = true;
            this.renderer.autoClear = true;
        }
        
        const c = this.camLight ? this.directionalLight.shadow.camera : this.camera;

        if (this.composer !== null){
            //this.renderer.setRenderTarget(this.composer.readBuffer); 
            this.composer.render();
        }else
            this.renderer.render(this.scene, c);
      
            
       if (this.renderMainScene === true) {

            //this.renderer.setRenderTarget(null);
            this.renderer.preserveDrawingBuffer = true;
            this.renderer.autoClear = false;
             this.renderer.clearDepth();
            this.renderer.render(this.CameraSpaceScene, c);
        }

        //
 
        if ((!this.isVr) && (this.sceneUI)) {

            this.renderer.setRenderTarget(null);
            this.renderer.alpha = true;

            if (this.renderMainScene === true) {
                this.renderer.preserveDrawingBuffer = true;
                this.renderer.autoClear = false;
            } else {
                this.renderer.preserveDrawingBuffer = false;
                this.renderer.autoClear = true;
            }

            this.renderer.autoClearDepth = true;

            this.renderer.render(this.sceneUI, this.cameraUI);
        }


        if (this.numFrames > 2)
            this.initPostProcessing(this.postProcessing);

        this.numFrames++;


    }

    setShadowProj(x, y, z) {
        this.directionalLight.shadow.camera.right = x;
        this.directionalLight.shadow.camera.left = -x;

        this.directionalLight.shadow.camera.top = -y;
        this.directionalLight.shadow.camera.bottom = y;

        this.directionalLight.shadow.camera.near = 10;
        this.directionalLight.shadow.camera.far = z;


        this.directionalLight.shadow.camera.updateProjectionMatrix();
    }

    setEmission(m, name, r, g, b, i = null) {

        const rtEngine = this.rtEngine;

        function editmat (mesh) {

            if (!mesh.isMesh)
                return;


            if (mesh.material.length > 0) {

                for (let m of mesh.material) {
                    if ((name === null) || (m.name === name)) {

                        if (r !== null)
                            m.emissive.r = r;

                        if (g !== null)
                            m.emissive.g = g;

                        if (b !== null)
                            m.emissive.b = b;

                        if (i !== null)
                            m.emissiveIntensity = i;

                        if (rtEngine) {
                            rtEngine.setEmission(mesh, name, r, g, b, i);
                        }

                    }
                }

            } else {

                if ((name === null) || (mesh.material.name === name)) {
                    if (r !== null)
                        mesh.material.emissive.r = r;

                    if (g !== null)
                        mesh.material.emissive.g = g;

                    if (b !== null)
                        mesh.material.emissive.b = b;

                    if (i !== null)
                        mesh.material.emissiveIntensity = i;
                }
            }
        }

        if(m.children.length>0)
            m.traverse(editmat);
        else
            editmat(m);
    }

    getColor(mesh, name) {
        if (mesh.material.length > 0) {

            for (let m of mesh.material) {
                if ((name === null) || (m.name === name)) {
                    return m.color;
                }
            }
        } else
            return mesh.material.color

        return null;
    }

    setTex(mesh, name, tex) {

        if (mesh.material.length > 0) {

            for (let m of mesh.material) {
                if ((name === null) || (m.name === name)) {
                    m.map = tex;
                    m.map.needsUpdate = true;
                    m.needsUpdate = true;
                    break;
                }
            }

        } else {
            mesh.material.map = tex;
            mesh.material.map.needsUpdate = true;
            mesh.material.needsUpdate = true;
        }

        if ((this.rtEngine) && (mesh.rtMesh)) {
            this.rtEngine.setTex(mesh, name, tex);
        }

    }

    updateTex(mesh, name) {

        if (mesh.material.length > 0) {

            for (let m of mesh.material) {
                if ((name === null) || (m.name === name)) {

                    if(m.map)
                    m.map.needsUpdate = true;
                    break;
                }
            }

        } else {

            if(mesh.material.map)
                mesh.material.map.needsUpdate = true;
        }

        if ((this.rtEngine) && (mesh.rtMesh)) {
            this.rtEngine.updateTex(mesh, name);
        }

    }

    updateETex(mesh, name) {

        if (mesh.material.length > 0) {

            for (let m of mesh.material) {
                if ((name === null) || (m.name === name)) {
                    m.emmisveIntensity = 0.1;
                    m.emissiveMap.needsUpdate = true;
                    return;
                }
            }

        } else {
            mesh.material.emissiveMap.needsUpdate = true;
        }
    }


    setColor(mesh, name, r, g, b) {
        if (mesh.material.length > 0) {

            for (let m of mesh.material) {
                if ((name === null) || (m.name === name)) {

                    if (r !== null)
                        m.color.r = r;

                    if (g !== null)
                        m.color.g = g;

                    if (b !== null)
                        m.color.b = b;

                }
            }

        } else {
            if (r !== null)
                mesh.material.color.r = r;

            if (g !== null)
                mesh.material.color.g = g;

            if (b !== null)
                mesh.material.color.b = b;

        }
    }

    setTextureOffset(mesh, name, offsetx = null, offsety = null, sizex = null, sizey = null) {

        function updateMat(material, offsetx = null, offsety = null, sizex = null, sizey = null) {

            if (material.map) {
                if (offsetx !== null)
                    material.map.offset.x = offsetx;

                if (offsety !== null)
                    material.map.offset.y = offsety;

                if (sizex !== null)
                    material.map.repeat.x = sizex;

                if (sizey !== null)
                    material.map.repeat.y = sizey;
            }

            if (material.alphaMap) {
                if (offsetx !== null)
                    material.alphaMap.offset.x = offsetx;

                if (offsety !== null)
                    material.alphaMap.offset.y = offsety;

                if (sizex !== null)
                    material.alphaMap.repeat.x = sizex;

                if (sizey !== null)
                    material.alphaMap.repeat.y = sizey;

                //material.alphaMap.needsUpdate = true;
            }

            if (material.normalMap) {
                if (offsetx !== null)
                    material.normalMap.offset.x = offsetx;

                if (offsety !== null)
                    material.normalMap.offset.y = offsety;

                if (sizex !== null)
                    material.normalMap.repeat.x = sizex;

                if (sizey !== null)
                    material.normalMap.repeat.y = sizey;

                //material.normalMap.needsUpdate = true;
            }


            if (material.emissiveMap) {
                if (offsetx !== null)
                    material.emissiveMap.offset.x = offsetx;

                if (offsety !== null)
                    material.emissiveMap.offset.y = offsety;

                if (sizex !== null)
                    material.emissiveMap.repeat.x = sizex;

                if (sizey !== null)
                    material.emissiveMap.repeat.y = sizey;

                material.emissiveMap.needsUpdate = true;
            }

            //material.map.needsUpdate = true;
            material.needsUpdate = true;
        }

        if (mesh.material.length > 0) {

            for (let m of mesh.material) {

                if ((name == null) || (m.name.startsWith(name)))
                    updateMat(m, offsetx, offsety, sizex, sizey);
            }

        } else {

            if ((name == null) || (mesh.material.name.startsWith(name)))
                updateMat(mesh.material, offsetx, offsety, sizex, sizey)

        }

        if ((this.rtEngine) && (mesh.rtMesh)) {
            this.rtEngine.setTextureOffset(mesh, name, offsetx, offsety, sizex, sizey);
        }


    }

    setShadowBias(bias) {
        this.directionalLight.shadow.bias = bias;
    }

    setSunLight(pos, target = null) {
        //const directionalLight = new THREE.SpotLight(0xffa95c,4);

        this.directionalLight.position.x = pos.x;
        this.directionalLight.position.y = pos.y;
        this.directionalLight.position.z = pos.z;

        if (target !== null)
            this.directionalLight.target = target;

        this.directionalLight.castShadow = true;

        //this.directionalLight.intensity = 1.2;
        this.directionalLight.power = 4.4;
        this.directionalLight.shadow.mapSize.width = 4096;
        this.directionalLight.shadow.mapSize.height = 4096;

        this.directionalLight.shadow.camera.near = -1000;
        this.directionalLight.shadow.camera.far = 1000;
        this.directionalLight.shadow.camera.right = 100;
        this.directionalLight.shadow.camera.left = -100;

        this.directionalLight.shadow.camera.top = -100;
        this.directionalLight.shadow.camera.bottom = 100;
        this.directionalLight.shadow.bias = 0.0000001;


        this.scene.add(this.directionalLight);

        /*
        const helper = new THREE.CameraHelper(directionalLight.shadow.camera)
        this.scene.add(helper)        
        */
    }

    setShadowSource(position, target = null) {

        /*
        this.ShadowViewMat.identity();
        this.ShadowViewMat.lookAt(new THREE.Vector3(position.x, position.y, position.z ), new THREE.Vector3(target.x, target.y, target.z ), new THREE.Vector3(0, 0, 1 ));
        this.ShadowViewMat.setPosition(position.x, position.y, position.z);
        */

        this.directionalLight.position.x = position.x;
        this.directionalLight.position.y = position.y;
        this.directionalLight.position.z = position.z;

        if (target !== null)
            this.directionalLight.target = target;
        else {

            if (this.shadowTarget == null) {
                const geo = new THREE.SphereGeometry(0.1);
                this.shadowTarget = new THREE.Mesh(geo);
                this.shadowTarget.name ='shadow target';
                this.scene.add(this.shadowTarget);
            }
            this.shadowTarget.position.set(0, 0, 0)
            this.directionalLight.target = this.shadowTarget;

            //this.directionalLight.target = null;
        }

    }


    removeChar(char) {

        if (char === null)
            return;

        this.scene.remove(char.avatarMesh);
    }

    removePod() {
        if (this.podMesh)
            this.scene.remove(this.podMesh);
    }

    updateMetaballGrid(Grid) {


        const MetaGrid = Grid.MetaGrid;
        let nt = 0;
        let nv = 0;
        let lastGoodTri = 0;
        MetaGrid.vIndices.fill(0xFFFFFFFF, 0, Grid.lastvIdx);

        Grid.lastvIdx = 0;

        for (let i = 0; i < MetaGrid.w; i++) {

            for (let j = 0; j < MetaGrid.h; j++) {

                for (let k = 0; k < MetaGrid.d; k++) {
                    //const Cell=MetaGrid.getCell(i,j,k);

                    const ind = MetaGrid.calcindex(i, j, k) * 22;
                    const cubeindex = MetaGrid.Cells[ind + 20]

                    const pt = triTable[cubeindex];

                    for (let n = 0; pt[n] != -1; n += 3) {

                        if (MetaGrid.Cells[ind + pt[n + 0]] === 0xFFFFFFFF)
                            continue;

                        if (MetaGrid.Cells[ind + pt[n + 1]] === 0xFFFFFFFF)
                            continue;

                        if (MetaGrid.Cells[ind + pt[n + 2]] === 0xFFFFFFFF)
                            continue;

                        this.eidxs[0] = pt[n + 0];
                        this.eidxs[1] = pt[n + 1];
                        this.eidxs[2] = pt[n + 2];

                        const i0 = MetaGrid.Edges[MetaGrid.Cells[ind + this.eidxs[0]] + 0] * 3;
                        const i1 = MetaGrid.Edges[MetaGrid.Cells[ind + this.eidxs[1]] + 0] * 3;
                        const i2 = MetaGrid.Edges[MetaGrid.Cells[ind + this.eidxs[2]] + 0] * 3;

                        this.v1.set(MetaGrid.Vertices[i0 + 0] - MetaGrid.Vertices[i1 + 0],
                            MetaGrid.Vertices[i0 + 1] - MetaGrid.Vertices[i1 + 1],
                            MetaGrid.Vertices[i0 + 2] - MetaGrid.Vertices[i1 + 2])

                        this.v2.set(MetaGrid.Vertices[i2 + 0] - MetaGrid.Vertices[i1 + 0],
                            MetaGrid.Vertices[i2 + 1] - MetaGrid.Vertices[i1 + 1],
                            MetaGrid.Vertices[i2 + 2] - MetaGrid.Vertices[i1 + 2])

                        this.norm.crossVectors(this.v1, this.v2);
                        this.norm.normalize();

                        for (let v = 0; v < 3; v++) {

                            const id = MetaGrid.Edges[MetaGrid.Cells[ind + this.eidxs[v]] + 0];
                            const idx = id * 3;

                            if (MetaGrid.vIndices[id] === 0xFFFFFFFF) {

                                Grid.geo.attributes.normal.array[nv * 3 + 0] = this.norm.x;
                                Grid.geo.attributes.normal.array[nv * 3 + 1] = this.norm.y;
                                Grid.geo.attributes.normal.array[nv * 3 + 2] = this.norm.z;

                                Grid.geo.attributes.position.array[nv * 3 + 0] = MetaGrid.Vertices[idx + 0];
                                Grid.geo.attributes.position.array[nv * 3 + 1] = MetaGrid.Vertices[idx + 1];
                                Grid.geo.attributes.position.array[nv * 3 + 2] = MetaGrid.Vertices[idx + 2];

                                MetaGrid.vIndices[id] = nv++;

                                Grid.lastvIdx = Math.max(Grid.lastvIdx, id);
                            } else {
                                Grid.geo.attributes.normal.array[MetaGrid.vIndices[id] * 3 + 0] += this.norm.x;
                                Grid.geo.attributes.normal.array[MetaGrid.vIndices[id] * 3 + 1] += this.norm.y;
                                Grid.geo.attributes.normal.array[MetaGrid.vIndices[id] * 3 + 2] += this.norm.z;
                            }

                            Grid.geo.index.array[nt] = MetaGrid.vIndices[id];



                            nt++;
                        }
                    }
                }
            }
        }

        /*
        for(let n = 0;n< nv; n++){

            const x = Grid.geo.attributes.normal.array[n * 3 + 0 ];
            const y = Grid.geo.attributes.normal.array[n * 3 + 1 ];
            const z = Grid.geo.attributes.normal.array[n * 3 + 2 ];
            const d = 1.0 / Math.sqrt(x*x + y*y + z*z);

            Grid.geo.attributes.normal.array[nv * 3 + 0 ] *= d;
            Grid.geo.attributes.normal.array[nv * 3 + 1 ] *= d;
            Grid.geo.attributes.normal.array[nv * 3 + 2 ] *= d;
        }
            */

        Grid.geo.drawRange.count = nt * 0.9;

        Grid.geo.index.needsUpdate = true;
        Grid.geo.attributes.position.needsUpdate = true;
        Grid.geo.attributes.normal.needsUpdate = true;
    }

    

    removeCar(car) {

        if (car === null)
            return;

        if (car.CarLight) {
            this.removeLight(car.CarLight);
        }

        this.removeMesh(car.contactMesh);

        if(car.weapon){

            if(this.rtEngine){
                this.rtEngine.removeMesh(car.weapon.body.rtMesh);
                this.rtEngine.removeMesh(car.weapon.head.rtMesh);
                this.rtEngine.removeMesh(car.weapon.canon.rtMesh);
            }
        }

        if(car.radar){
            if(this.rtEngine){
                this.rtEngine.removeMesh(car.radar.rtMesh);
            }
        }
         
        for (let n = 0; n < car.wheels.length; n++) {

            if (car.wheels[n].mesh) {
                this.scene.remove(car.wheels[n].mesh);

                if(car.wheels[n].parts)
                    this.scene.remove(car.wheels[n].parts.particleMesh);

                if ((this.rtEngine) && (car.wheels[n].mesh.rtMesh)) {
                    this.rtEngine.removeMesh(car.wheels[n].mesh.rtMesh);
                }
            }

            this.scene.remove(car.wheels[n].tireNameMesh);
            this.scene.remove(car.wheels[n].tireCubeMesh);
        }

        let s;
        while(s = this.sndMeshes.pop()){
            this.scene.remove(s);
        }

         if (car.ChassisMesh) {
            if ((this.rtEngine) && (car.ChassisMesh.rtMesh)) {
                this.rtEngine.removeMesh(car.ChassisMesh.rtMesh);
            }

            this.scene.remove(car.ChassisMesh);

        }

        /*
        if (car.carDef.radar) {
            this.scene.remove(car.radar);
        }
        */


        if (car.DMGBar1)
            this.scene.remove(car.DMGBar1);

        if (car.DMGBar2)
            this.scene.remove(car.DMGBar2);

        this.scene.remove(car.nameMesh);

        car.skidPlanes = [];

        if (car.exhaustParticles) {
            for (let n = 0; n < this.particles.length; n++) {
                if (this.particles[n].id == car.exhaustParticles.id) {

                    this.scene.remove(this.particles[n].particleMesh)
                    this.particles[n].particleGeometry.dispose();

                    this.particles.splice(n, 1);
                    break;
                }
            }
        }

        this.removeTireBox();
        this.removeMotorBox();
        this.removeChassisBox();
        this.removeBatteryBox();
        this.removeRadarBox();
        this.removeWeaponBox();
    }

    addCarObj(car) {

        this.sc = false;

       

        var eul = new THREE.Euler(0, 0, 0, "XYZ");
        eul.setFromQuaternion(car.quat);

        car.ChassisMesh.rotation.x = eul.x;
        car.ChassisMesh.rotation.y = eul.y;
        car.ChassisMesh.rotation.z = eul.z;


        const mi = car.getMeshInfos();


        car.ChassisMesh.position.set(car.pos.x, car.pos.y, car.pos.z);

        /*car.ChassisMesh.material.envMap = this.ldrCubeMap;*/
        car.ChassisMesh.material.needsUpdate = true;

        car.ChassisMesh.castShadow = true;
        car.ChassisMesh.receiveShadow = true;


        car.target = { id: this.targetID++, type: 'car', name: car.name ? car.name : 'car-' + car.id, subsystems: [], pos: { x: car.ChassisMesh.position.x, y: car.ChassisMesh.position.y, z: car.ChassisMesh.position.z }, txt: null };
        const geo = new TextGeometry(car.target.name, { font: this.impactFont, size: 0.1, height: 0.1, curveSegments: 4 });
        geo.computeBoundingSphere();
        geo.translate(-geo.boundingSphere.radius, 0, 0);
        car.target.txt = new THREE.Mesh(geo, this.targetMat);



        for (let n = 0; n < car.wheels.length; n++) {

            car.wheels[n].mesh.scale.set(car.wheels[n].scale.x, 1, 1);

            if(this.rtEngine)
                this.rtEngine.enableRT(car.wheels[n].mesh);

            this.scene.add(car.wheels[n].mesh);

            car.wheels[n].mesh.castShadow = true;
            car.wheels[n].mesh.receiveShadow = true;
            
            if (this.rtEngine) {
                car.wheels[n].mesh.rtMesh = this.rtEngine.addMesh(car.wheels[n].mesh, RT_COAT, new Transformation3(car.wheels[n].mesh.position, car.wheels[n].mesh.quaternion));
                this.rtEngine.drawMesh(car.wheels[n].mesh.rtMesh);

                if(car.wheels[n].mesh.rtMesh)
                    this.rtEngine.alwaysRender.push(car.wheels[n].mesh.rtMesh.geoID);
            }

        }

        if (car.weapon) {

            const weapon = car.weapon;

            //  weapon.body.meshGPU = this.addMesh(weapon.body, RT_DIFF, new Transformation3(weapon.body.position));

            weapon.canon.position.set(weapon.canonRel.x, weapon.canonRel.y, weapon.canonRel.z);
            //weapon.canon.meshGPU = this.addMesh(weapon.canon, RT_DIFF, new Transformation3(weapon.canon.position));

            weapon.head.position.set(weapon.headRel.x, weapon.headRel.y, weapon.headRel.z);
            //weapon.head.meshGPU = this.addMesh(weapon.head, RT_DIFF, new Transformation3(weapon.head.position));

            this.addMeshChild(weapon.head, weapon.canon);
            this.addMeshChild(weapon.body, weapon.head);
            this.addMeshChild(car.ChassisMesh, weapon.body);


            if (this.rtEngine) {

                weapon.body.updateMatrix();
                weapon.body.updateMatrixWorld(true);
                weapon.body.updateWorldMatrix(true, true);

                weapon.head.updateMatrix();
                weapon.head.updateMatrixWorld(true);
                weapon.head.updateWorldMatrix(true, true);

                weapon.canon.updateMatrix();
                weapon.canon.updateMatrixWorld(true);
                weapon.canon.updateWorldMatrix(true, true);

                weapon.body.rtMesh = this.rtEngine.addMesh(weapon.body, RT_COAT, this.mat4toTransform(weapon.body.matrixWorld));
                this.rtEngine.drawMesh(weapon.body.rtMesh);

                if(weapon.body.rtMesh)
                    this.rtEngine.alwaysRender.push(weapon.body.rtMesh.geoID);


                weapon.head.rtMesh = this.rtEngine.addMesh(weapon.head, RT_COAT, this.mat4toTransform(weapon.head.matrixWorld));
                this.rtEngine.drawMesh(weapon.head.rtMesh);

                if(weapon.head.rtMesh)
                    this.rtEngine.alwaysRender.push(weapon.head.rtMesh.geoID);


                weapon.canon.rtMesh = this.rtEngine.addMesh(weapon.canon, RT_COAT, this.mat4toTransform(weapon.canon.matrixWorld));
                this.rtEngine.drawMesh(weapon.canon.rtMesh);

                if(weapon.canon.rtMesh)
                    this.rtEngine.alwaysRender.push(weapon.canon.rtMesh.geoID);                
            }


            car.weapon.shotSphere = {};

            this.addSphere(car.weapon.shotSphere,
                car.weapon.projSize,
                new THREE.Vector3(0, 0, 0),
                new THREE.Vector3(0.2, 0.8, 0.8),
                new THREE.Vector3(0.2, 0.8, 0.8),
                0.2,
                RT_SPEC
            );

            car.weapon.shotSphere.object.name ='car shot';
            this.setMeshStatic(car.weapon.shotSphere.object, false);

            if (this.rtEngine) {
                car.weapon.shotSphere.rtSphere = {};
                this.rtEngine.addSphere(car.weapon.shotSphere.rtSphere, car.weapon.projSize, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 1, 1), new THREE.Vector3(0, 1, 1), 0.2, RT_SPEC);
            }
        }

        if (car.radar) {

            const radar = car.radar;
            //radar.meshGPU = this.addMesh(radar, RT_DIFF, new Transformation3(radar.position));
            this.addMeshChild(car.ChassisMesh, radar);

            if(this.rtEngine){
                radar.updateMatrix();
                radar.updateMatrixWorld();

                radar.rtMesh = this.rtEngine.addMesh(radar, RT_COAT, this.mat4toTransform(radar.matrixWorld));
                this.rtEngine.drawMesh(radar.rtMesh);

                if(radar.rtMesh)
                    this.rtEngine.alwaysRender.push(radar.rtMesh.geoID);
            }

        }

        car.contactMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFF00, roughness: 0.1, opacity: 0.75, transparent: true, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false })
        car.contactGeom = new THREE.SphereGeometry(0.1, 32, 16);
        car.contactMesh = new THREE.InstancedMesh(car.contactGeom, car.contactMaterial, 100);
        car.contactMesh.name= 'carcontacts-'+car.id;


        const m = new THREE.Matrix4();
        m.identity();
        m.scale(0, 0, 0);

        for (let n = 0; n < 100; n++) {
            car.contactMesh.setMatrixAt(n, m);
        }


        if (this.rtEngine) {

            this.rtEngine.enableRT(car.ChassisMesh);

            car.ChassisMesh.rtMesh = this.rtEngine.addMesh(car.ChassisMesh, RT_COAT, new Transformation3(car.ChassisMesh.position, car.ChassisMesh.quaternion));
            this.rtEngine.drawMesh(car.ChassisMesh.rtMesh);

            if(car.ChassisMesh.rtMesh)
                this.rtEngine.alwaysRender.push(car.ChassisMesh.rtMesh.geoID);
        }

        this.addMesh(car.contactMesh);
        this.addMesh(car.ChassisMesh);

        return true;
    }



    async addCar(myCar, mode, carColor = 0) {

        await this.loadCar(myCar, mode);
        this.addCarObj(myCar);

        const epos = new THREE.Vector3();

        myCar.getExhaustPos(epos);
        const particles = new ParticleEngine(epos);
        particles.name = 'exhaust';
        particles.particlesPerSecond = 0;
        particles.pinned = true;

        particles.setValues(smoke_params);
        particles.initialize();

        myCar.exhaustParticles = { particles: particles };
        this.scene.add(particles.particleMesh);

        this.addSpotLight(myCar.CarLight,
            new THREE.Vector3(myCar.pos.x, myCar.pos.y, myCar.pos.z),
            new THREE.Vector3(0.8, 0.8, 0.0).multiplyScalar(1),
            12.5,
            2.0);


        for (let w of myCar.wheels) {
            w.parts = new ParticleEngine(w.pos, true);
            w.parts.name = 'parts-wheel';
            w.parts.setValues(drift_params);
            w.parts.initialize();
            this.scene.add(w.parts.particleMesh);

            //this.particles.push(w.parts);
        }




        this.particles.push(particles);

        //if(myCar.isAI) 
        {
            /*
            const geo1 = new THREE.BoxGeometry(3, 0.8, 0.8);
            myCar.DMGBar1 = new THREE.Mesh(geo1, this.dmgMaterial);

            const geo2 = new THREE.BoxGeometry(3, 0.4, 0.4);
            myCar.DMGBar2 = new THREE.Mesh(geo2, this.dmgMaterial2);

            myCar.DMGBar1.position.set(myCar.ChassisMesh.position.x, myCar.ChassisMesh.position.y + 2.2, myCar.ChassisMesh.position.z);
            myCar.DMGBar2.position.set(myCar.ChassisMesh.position.x, myCar.ChassisMesh.position.y + 2.2, myCar.ChassisMesh.position.z);

            this.scene.add(myCar.DMGBar1);
            this.scene.add(myCar.DMGBar2);
            */

            const geo = new TextGeometry(myCar.target.name, { font: this.speedFont, size: 0.8, height: 0.1, curveSegments: 4 });
            geo.computeBoundingSphere();
            geo.translate(-geo.boundingSphere.radius, 0, 0);

            myCar.color = new THREE.Color().setHex(carColors[carColor]);

            myCar.nameMat = new THREE.MeshPhongMaterial({ color: myCar.color.clone(), emissive: myCar.color.clone(), emissiveIntensity: 4.0 })

            myCar.nameMesh = new THREE.Mesh(geo, myCar.nameMat);

            this.scene.add(myCar.nameMesh);

        }

    }


    async setCar(myCar, mode = 0) {


        await this.loadCar(myCar, mode);
        await this.addCarObj(myCar);


        if (this.tireNameMesh) {
            for (let wheel of myCar.wheels) {

                wheel.tireNameMesh = this.tireNameMesh.clone();
                wheel.tireCubeMesh = this.tireCubeMesh.clone();

                this.addMeshChild(wheel.mesh, wheel.tireNameMesh);
                this.addMeshChild(wheel.mesh, wheel.tireCubeMesh);
            }

            this.scene.remove(this.tireNameMesh);
            this.scene.remove(this.tireCubeMesh);
        }


        this.podMesh.position.y = myCar.wheels[0].pos.y - myCar.wheelRadius - 0.27



        const epos = new THREE.Vector3();
        myCar.getExhaustPos(epos);
        const particles = new ParticleEngine(epos);
        particles.setValues(smoke_params);
        particles.initialize();

        particles.name = 'exhaust';
        particles.pinned = true;
        particles.particlesPerSecond = 0;

        myCar.exhaustParticles = { particles: particles };

        this.scene.add(particles.particleMesh);

        this.particles.push(particles);
    }



    updateRain(time, dt, roadObj, pos = null, dx= 0, dz= 0) {

        if (!this.rainMesh)
            return;

        const m = new THREE.Matrix4();


        for (let n = 0; n < this.numDrops; n++) {

            this.dropsArray[n * 3 + 1] -= 40.0 * dt;

            if((dx)&&(dz)){
                this.dropsArray[n * 3 + 0] += dx * dt;
                this.dropsArray[n * 3 + 2] += dz * dt;

            }
            //this.rainMesh.geometry.attributes.position.array[n*3+4] -= 40.0 * dt;

            const ry = this.dropsArray[n * 3 + 1];

            if (this.dropDist[n]) {

                if (ry < this.dropDist[n].y) {

                    for (let d = 0; d < 8; d++) {

                        const a = d * Math.PI * 2.0 / 8.0 + Math.random();
                        const dx = Math.cos(a);
                        const dy = Math.sin(a);

                        this.dropMarks.push({ x: this.dropDist[n].x, y: this.dropDist[n].y, z: this.dropDist[n].z, dx: dx, dy: dy, time: time });
                    }

                    this.resetDrop(this.dropsArray, n, roadObj, pos);
                }

            } else if (ry < -10) {
                this.resetDrop(this.dropsArray, n, roadObj, pos);
            }

            m.identity();
            m.makeTranslation(this.dropsArray[n * 3 + 0], this.dropsArray[n * 3 + 1], this.dropsArray[n * 3 + 2]);
            this.rainMesh.setMatrixAt(n, m);
        }

        /*
       
        let n = 0;
        while (n < this.dropMarks.length) {
            const ddt = time - this.dropMarks[n].time;
            if (ddt > 0.5) {

                //this.removeSphere(this.dropMarks[n].mesh, false);
                //this.scene.remove(this.dropMarks[n].mesh);
                this.dropMarks.splice(n, 1);
            } else {

                this.dropMarks[n].x += this.dropMarks[n].dx * dt;
                this.dropMarks[n].z += this.dropMarks[n].dy * dt;

                const y = this.dropMarks[n].y + (Math.sin(ddt * 2.0 * Math.PI) + 1.0) * 0.5;

                m.identity();
                m.makeTranslation(this.dropMarks[n].x, y ,this.dropMarks[n].z);

                this.rainDropMesh.setMatrixAt(n, m);

                /*
                if(this.rtEngine){
                    const sphere = {
                                    object : {
                                        position: {x :this.dropMarks[n].x, y:y ,z:this.dropMarks[n].z},
                                      
                                    },  mat : {
                                            color : {r:this.rainColor.x, g : this.rainColor.y, b: this.rainColor.z}
                                        },
                                    sphere:{
                                        radius : 0.05,
                                        roughness : 0.05,
                                        emission : {r : this.rainEmm.x, g : this.rainEmm.y, b : this.rainEmm.z},
                                        type: 1
                                    }
                                }

                    this.rtEngine.drawMesh(sphere);
                }*


      

                n++;
            }
        }

        m.identity();
        m.scale(0, 0, 0);

        for (let n = this.dropMarks.length; n < this.numDrops * 8; n++) {
            this.rainDropMesh.setMatrixAt(n, m);
        }

        this.rainDropMesh.instanceMatrix.needsUpdate = true;
        */
       this.rainMesh.instanceMatrix.needsUpdate = true;

        //this.rainMesh.geometry.attributes.position.needsUpdate = true;
    }

    resetRain(roadObj, pos) {
        this.numFloorDrop = 0;
        this.dropMarks = [];

        const m = new THREE.Matrix4();

        m.identity();
        m.scale(0, 0, 0);

        for (let n = 0; n < this.numDrops * 8; n++) {

            for (let i = 0; i < 8; i++) {
                this.rainDropMesh.setMatrixAt(n * 8 + i, m);
            }
            this.resetDrop(this.dropsArray, n, roadObj, pos);
        }
    }


    async createRain(numDrops, roadObj, pos) {

        this.numDrops = numDrops;
        this.numFloorDrop = 0;

        this.dropsArray = new Float32Array(numDrops * 3);

        this.dropR = new THREE.Vector3();
        this.dropRc = new THREE.Raycaster();
        this.dropO = new THREE.Vector3();
        this.dropD = new THREE.Vector3(0, -1, 0);

        this.rainGeom = new THREE.SphereGeometry(0.05, 32, 16);
        this.rainMat = new THREE.MeshPhongMaterial({ color: new THREE.Color().setRGB(this.rainColor.x, this.rainColor.y, this.rainColor.z), emissive: new THREE.Color().setRGB(this.rainEmm.x, this.rainEmm.y, this.rainEmm.z), emissiveIntensity : 4.0 });
        this.rainDropMesh = new THREE.InstancedMesh(this.rainGeom, this.rainMat, this.numDrops * 8);
        this.rainDropMesh.name='raindrops';

        this.rainDropMesh.frustumCulled = false;

        const gltf = await this.loadGLTF("drop.glb");
        this.dropMesh = this.deGroupGLB(gltf.children[0]);
        this.dropGeo = this.dropMesh.children[0].geometry;

        const m = new THREE.Matrix4();
        m.scale(0, 0, 0);

        for (let n = 0; n < numDrops; n++) {

            this.resetDrop(this.dropsArray, n, roadObj, pos);

            for (let i = 0; i < 8; i++) {

                this.rainDropMesh.setMatrixAt(n * 8 + i, m);
            }
        }

        /*
        const rainGeo = new THREE.BufferGeometry();
        rainGeo.setAttribute('position', new THREE.Float32BufferAttribute(drops, 3));
        this.rainMesh = new THREE.Points(rainGeo, this.RainDropMat);
        */

       

        this.rainMesh = new THREE.InstancedMesh(this.dropGeo, this.rainMat, numDrops);
        this.rainMesh.name= 'rainmesh';
        this.rainMesh.frustumCulled = false;
        this.rainMesh.smearIntensity = 10.0;
        this.rainMesh.samples = 107;
        this.rainMesh.jitter = 1;


    }



    addCharObj(char) {

        this.sc = false;

        char.target = { id: this.targetID++, eid : char.id,  type: 'npc', name: 'npc-' + char.id, subsystems: [], pos: { x: char.pos.x, y: char.pos.y + (char.avatarDef.height * char.skelScale) / 2 + char.worldOffset.y * 2, z: char.pos.z } };

        if (char.skeleton) {

            if (char.originalMesh === null){
                char.originalMesh = char.avatarMesh.clone();
            }
                

            const infos = this.bindSkeleton(char.skeleton, char.originalMesh, char.skelScale);
            char.tskel = infos.skel;
            char.avatarMesh = infos.mesh;

            if (char.subsystemsDef) {
                this.findSubsystemsBone(char.skeleton.root, char.subsystemsDef, char.target.subsystems);
            }
            char.avatarMesh.position.set(char.pos.x, char.pos.y, char.pos.z);
        }else if (char.nInstances > 0) {
            const m = new THREE.Matrix4();
            m.makeTranslation(char.pos.x, char.pos.y, char.pos.z);
            char.avatarMesh.setMatrixAt(char.instanceID, m);
        } else {
            char.avatarMesh.position.set(char.pos.x, char.pos.y, char.pos.z);
        }

        char.avatarMesh.castShadow = true;
        char.avatarMesh.receiveShadow = false;

        if ((char.instanceID < 1) || (char.skeleton)) {
            this.scene.add(char.avatarMesh);
            if (this.rtEngine) {

                if(!char.avatarMesh.rtMesh)
                    char.avatarMesh.rtMesh = new Array(char.nInstances);

                char.avatarMesh.rtMesh[char.instanceID] = this.rtEngine.addMesh(char.avatarMesh, RT_COAT, new Transformation3(char.avatarMesh.position, char.avatarMesh.quaternion));
                this.rtEngine.drawMesh(char.avatarMesh.rtMesh[char.instanceID]);
                this.rtEngine.alwaysRender.push(char.avatarMesh.rtMesh[char.instanceID].geoID);
            }
        }

        return true;
    }
    async addChar(myChar) {

        await this.loadCharacter(myChar);
        this.addCharObj(myChar);
    }

    async setChar(myChar) {

        await this.loadCharacter(myChar);
        this.addCharObj(myChar);
    }
    scene;
    camFollowCar;
}

export { EngineMain };